import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Offcanvas, Button, Form, Nav, Row, Col, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faTrash } from '@fortawesome/free-solid-svg-icons';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import DatePicker from 'react-datepicker';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import moment from 'moment';
import Select, { components } from 'react-select';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import {
    fetchTemplatesByCustomer,
    dispatchBatchNotification,
    PlanningConflictModal,
    checkEmployeeAvailability,
    fetchRecommendedEmployees,
    partitionRecommendedEmployees,
    sortEmployeesAscending,
    clearEmployeeAvailabilityCache,
} from '../../assets/js/common';
import { setHours, setMinutes } from 'date-fns';
import { getNthWeekdayOfMonth, getWeekdayKey, getOccurrenceLabel, getOccurrenceCount, localTimeToUtcHHmm, utcHHmmToLocal } from '../../utils/recurrenceHelpers';
import TimeInput from '../proposals/timeInput';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const normalizeTime = (date) =>
    moment(date).set({ year: 2000, month: 0, date: 1 });

const generateOrderNumber = () => {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = String(now.getFullYear()).slice(-2);
    const hour = String(now.getHours()).padStart(2, '0');
    const second = String(now.getSeconds()).padStart(2, '0');
    const milli = String(now.getMilliseconds()).padStart(3, '0');
    return `O${day}${month}${year}${hour}${second}${milli}`;
};

// ─── New Proposal Inline Form ────────────────────────────────────────────────

const DAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const getWeekdayOccurrenceOfMonth = (year, month, occurrence, weekday) => {
    const dayIndex = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"]
      .indexOf(weekday.toLowerCase());
    if (dayIndex === -1) return null;

    let date = moment().year(year).month(month).date(1).startOf("day");
    const matchedDates = [];

    while (date.month() === month) {
      if (date.day() === dayIndex) {
        matchedDates.push(date.clone());
      }
      date.add(1, "day");
    }

    if (occurrence === "1st") return matchedDates[0];
    if (occurrence === "2nd") return matchedDates[1];
    if (occurrence === "3rd") return matchedDates[2];
    if (occurrence === "4th") return matchedDates[3];
    if (occurrence === "last") return matchedDates[matchedDates.length - 1];

    return null;
};

const getTaskOccurrenceDates = (task, orderStartDate, orderEndDate, taskSelectedDaysMap = {}, taskOneTimeDatesMap = {}) => {
    if (!orderStartDate) return [];
    const freq = task.recurrence?.frequency || task.Frequency;
    if (freq === "OneTime") {
        const override = taskOneTimeDatesMap[task.ProposalTaskId];
        const oneTimeDate = override || task.recurrence?.advancedRecurrence?.date;
        return [oneTimeDate ? moment(oneTimeDate) : moment(orderStartDate)];
    }

    const rangeEnd = orderEndDate ? moment(orderEndDate) : moment(orderStartDate).add(6, "months");
    const dates = [];

        if (freq === "Daily") {
      const adv = task.recurrence?.advancedRecurrence || {};
      const timesList = Array.isArray(adv.times) && adv.times.length > 0 ? adv.times : ["08:00"];

      let cursor = moment(orderStartDate).startOf("day");
      while (cursor.isSameOrBefore(rangeEnd, "day")) {
        const dateStr = cursor.format("YYYY-MM-DD");
        timesList.forEach((timeStr) => {
          const slot = moment.utc(`${dateStr}T${timeStr}:00`).local();
          if (slot.isSameOrAfter(orderStartDate, "day") && slot.isSameOrBefore(rangeEnd, "day")) {
            dates.push(slot);
          }
        });
        cursor.add(1, "day");
      }
    } else if (freq === "Weekly") {
      const days = taskSelectedDaysMap[task.ProposalTaskId] || task.recurrence?.selectedDays || [];
      if (!days.length) return [];
      let cursor = moment(orderStartDate).startOf("isoWeek");
      while (cursor.isSameOrBefore(rangeEnd, "day")) {
        days.forEach((day) => {
          const dayIndex = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"]
            .indexOf(day.toLowerCase());
          if (dayIndex === -1) return;
          const d = moment(cursor).add(dayIndex, "days");
          if (d.isSameOrAfter(orderStartDate, "day") && d.isSameOrBefore(rangeEnd, "day")) {
            dates.push(d);
          }
        });
        cursor.add(1, "week");
      }
    } else if (freq === "Monthly") {
      const adv = task.recurrence?.advancedRecurrence || {};
      const repeatEvery = task.recurrence?.repeatEvery || task.RepeatEvery || 1;
      let cursor = moment(orderStartDate).startOf("month");
      let monthCounter = 0;

      while (cursor.isSameOrBefore(rangeEnd, "month")) {
        if (monthCounter % repeatEvery === 0) {
          if (adv.monthlyType === "specific_day" || !adv.monthlyType) {
            const mDayRaw = adv.monthlyDay ?? task.recurrence?.monthlyDay ?? task.MonthlyDay ?? 1;
            const clampedDay = mDayRaw === "last" ? cursor.daysInMonth() : Math.min(Number(mDayRaw) || 1, cursor.daysInMonth());
            const targetDate = moment(cursor).date(clampedDay);
            if (targetDate.isSameOrAfter(orderStartDate, "day") && targetDate.isSameOrBefore(rangeEnd, "day")) {
              dates.push(targetDate);
            }
          } else {
            let occurrences = [];
            if (Array.isArray(adv.monthlyOccurrences)) {
              occurrences = adv.monthlyOccurrences;
            } else if (adv.monthlyWeekOccurrence && adv.monthlyWeekday) {
              const parts = adv.monthlyWeekOccurrence.split("_");
              parts.forEach(p => {
                occurrences.push({ occurrence: p, weekday: adv.monthlyWeekday });
              });
            }
            occurrences.forEach((occObj) => {
              const matchedDate = getWeekdayOccurrenceOfMonth(cursor.year(), cursor.month(), occObj.occurrence, occObj.weekday);
              if (matchedDate && matchedDate.isSameOrAfter(orderStartDate, "day") && matchedDate.isSameOrBefore(rangeEnd, "day")) {
                dates.push(matchedDate);
              }
            });
          }
        }
        cursor.add(1, "month");
        monthCounter++;
      }
    } else if (freq === "Yearly") {
      const adv = task.recurrence?.advancedRecurrence || {};
      let cursor = moment(orderStartDate).startOf("year");
      while (cursor.isSameOrBefore(rangeEnd, "year")) {
        const yearlyDateEntries = Array.isArray(adv.dates) && adv.dates.length > 0
          ? adv.dates
          : [{
              day: adv.yearlyDay ?? task.recurrence?.yearlyDay ?? task.YearlyDay ?? 1,
              month: Array.isArray(adv.yearlyMonth)
                ? adv.yearlyMonth[0]
                : (adv.yearlyMonth ?? task.recurrence?.yearlyMonth ?? task.YearlyMonth ?? 0),
            }];

        yearlyDateEntries.forEach((entry) => {
          const monthCursor = moment(cursor).month(Number(entry.month) || 0);
          const clampedDay = entry.day === "last"
            ? monthCursor.daysInMonth()
            : Math.min(Number(entry.day) || 1, monthCursor.daysInMonth());
          const targetDate = moment(monthCursor).date(clampedDay);
          if (targetDate.isSameOrAfter(orderStartDate, "day") && targetDate.isSameOrBefore(rangeEnd, "day")) {
            dates.push(targetDate);
          }
        });
        cursor.add(1, "year");
      }
    }

    return dates;
};

const MAX_DAILY_TIME_SLOTS = 6;
const MAX_MONTHLY_OCCURRENCE_ROWS = 4;
const MAX_YEARLY_OCCURRENCE_ROWS = 6;

const getDefaultDailyTime = () => setHours(setMinutes(new Date(), 0), 8);

const EMPTY_TASK_FORM = {
    taskId: '',
    taskName: '',
    frequency: 'Weekly',
    repeatEvery: 1,
    selectedDays: [],
    oneTimeDate: null,
    dailyTimes: [getDefaultDailyTime()],
    monthlyDay: new Date().getDate(),
    monthlyType: 'specific_day',
    monthlyOccurrences: [{ occurrence: "1st", weekday: "monday" }],
    yearlyDates: [{ day: new Date().getDate(), month: new Date().getMonth() }],
};

// ─── Shared Select props helper ───────────────────────────────────────────────
const getSelectPortalProps = () => ({
    menuPortalTarget: document.body,
    menuPosition: 'fixed',
    menuShouldScrollIntoView: false,
    styles: {
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    },
});

const NewProposalForm = React.forwardRef(({ t = (t) => t, workTypeOptions = [], taskOptions = [], paymentConditionOptions = [], invoiceTypeOptions = [], initialStartDate = null, onTaskStateChange }, ref) => {
    const { i18n } = useTranslation();
    const [workType, setWorkType] = useState(null);
    const [startDate, setStartDate] = useState(initialStartDate);
    const [paymentCondition, setPaymentCondition] = useState('Bank Transfer');
    const [invoiceType, setInvoiceType] = useState('Invoice');
    const [savedTasks, setSavedTasks] = useState([]);
    const [taskPendingDeleteId, setTaskPendingDeleteId] = useState(null);
    const [taskForm, setTaskForm] = useState(EMPTY_TASK_FORM);

    const isTaskSectionEnabled = !!workType && !!startDate;

    useEffect(() => {
        const refDate = startDate ? new Date(startDate) : new Date();
        const refDay = getWeekdayKey(refDate.getDay());
        const { nth, weekday } = getNthWeekdayOfMonth(refDate);
        const defaultOccurrence = { occurrence: getOccurrenceLabel(nth), weekday: getWeekdayKey(weekday) };

        setTaskForm(prev => {
            switch (prev.frequency) {
                case 'OneTime':
                    return prev.oneTimeDate === refDate ? prev : { ...prev, oneTimeDate: refDate };
                case 'Weekly':
                    return { ...prev, selectedDays: DAY_KEYS.includes(refDay) ? [refDay] : [] };
                case 'Monthly':
                    return { ...prev, monthlyDay: refDate.getDate(), monthlyOccurrences: [{ ...defaultOccurrence }] };
                case 'Yearly':
                    return {
                        ...prev,
                        yearlyDates: [{ day: refDate.getDate(), month: refDate.getMonth() }],
                    };
                default:
                    return prev;
            }
        });
    }, [startDate, taskForm.frequency]);

    const paymentConditionSelectOptions = [
        { value: 'Cash', label: t('cash') },
        { value: 'Bank Transfer', label: t('bankTransfer') },
        { value: 'Payment Request', label: t('paymentRequest') },
        ...paymentConditionOptions,
    ];

    const invoiceTypeSelectOptions = [
        { value: 'Invoice', label: t('invoice') },
        { value: 'Proforma Invoice', label: t('proformaInvoice') },
        ...invoiceTypeOptions,
    ];

const isTaskValid = (() => {
        if (!isTaskSectionEnabled) return false;
        if (!taskForm.taskId || !taskForm.frequency) return false;
        if (taskForm.frequency === 'Daily') {
            return taskForm.dailyTimes && taskForm.dailyTimes.length > 0;
        }
        if (taskForm.frequency === 'Weekly') {
            return taskForm.selectedDays.length > 0;
        }
        if (taskForm.frequency === 'Monthly') {
            return taskForm.monthlyType === "specific_day"
                ? Boolean(taskForm.monthlyDay)
                : (taskForm.monthlyOccurrences && taskForm.monthlyOccurrences.length > 0);
        }
        if (taskForm.frequency === 'Yearly') {
            return taskForm.yearlyDates && taskForm.yearlyDates.length > 0;
        }
        if (taskForm.frequency === 'OneTime') {
            return Boolean(taskForm.oneTimeDate);
        }
        return true;
    })();
    const handleFrequencyChange = (selected) => {
        const freq = selected ? selected.value : '';
        const refDate = startDate ? new Date(startDate) : new Date();
        let selectedDays = [];
        if (freq === 'Daily') {
            selectedDays = [...DAY_KEYS];
        } else if (freq === 'Weekly') {
            const refDay = getWeekdayKey(refDate.getDay());
            selectedDays = DAY_KEYS.includes(refDay) ? [refDay] : [];
        }

        const { nth, weekday } = getNthWeekdayOfMonth(refDate);
        const defaultOccurrence = { occurrence: getOccurrenceLabel(nth), weekday: getWeekdayKey(weekday) };

        setTaskForm(prev => ({
            ...prev,
            frequency: freq,
            selectedDays,
            repeatEvery: 1,
            oneTimeDate: freq === 'OneTime' ? refDate : prev.oneTimeDate,
            dailyTimes: [getDefaultDailyTime()],
            monthlyDay: refDate.getDate(),
            monthlyType: "specific_day",
            monthlyOccurrences: [{ ...defaultOccurrence }],
        }));
    };

    const addDailyTimeSlot = () => {
        setTaskForm(prev => {
            const currentTimes = prev.dailyTimes || [getDefaultDailyTime()];
            if (currentTimes.length >= MAX_DAILY_TIME_SLOTS) return prev;
            const lastTime = currentTimes[currentTimes.length - 1];
            const newTime = moment(lastTime).add(1, 'hour').toDate();
            return { ...prev, dailyTimes: [...currentTimes, newTime] };
        });
    };

    const updateDailyTimeSlot = (index, newTime) => {
        setTaskForm(prev => {
            const currentTimes = [...(prev.dailyTimes || [getDefaultDailyTime()])];
            currentTimes[index] = newTime;
            return { ...prev, dailyTimes: currentTimes };
        });
    };

    const removeDailyTimeSlot = (index) => {
        setTaskForm(prev => {
            const currentTimes = prev.dailyTimes || [getDefaultDailyTime()];
            if (currentTimes.length <= 1) return prev;
            return { ...prev, dailyTimes: currentTimes.filter((_, i) => i !== index) };
        });
    };

    const addMonthlyOccurrenceRow = () => {
        setTaskForm(prev =>
            (prev.monthlyOccurrences || []).length >= MAX_MONTHLY_OCCURRENCE_ROWS
                ? prev
                : { ...prev, monthlyOccurrences: [...(prev.monthlyOccurrences || []), { occurrence: "1st", weekday: "monday" }] }
        );
    };

    const removeMonthlyOccurrenceRow = (idx) => {
        setTaskForm(prev =>
            (prev.monthlyOccurrences || []).length > 1
                ? { ...prev, monthlyOccurrences: prev.monthlyOccurrences.filter((_, i) => i !== idx) }
                : prev
        );
    };

        const addYearlyDateRow = () => {
        setTaskForm(prev => ({
            ...prev,
            yearlyDates: (prev.yearlyDates || []).length >= MAX_YEARLY_OCCURRENCE_ROWS
                ? prev.yearlyDates
                : [...(prev.yearlyDates || []), { day: 1, month: 0 }]
        }));
    };

    const removeYearlyDateRow = (idx) => {
        setTaskForm(prev => ({
            ...prev,
            yearlyDates: (prev.yearlyDates || []).length > 1
                ? prev.yearlyDates.filter((_, i) => i !== idx)
                : prev.yearlyDates
        }));
    };

       const toggleDay = (dayKey) => {
        setTaskForm(prev => ({
            ...prev,
            selectedDays: prev.selectedDays.includes(dayKey)
                ? prev.selectedDays.filter(d => d !== dayKey)
                : [...prev.selectedDays, dayKey]
        }));
    };
    const getWeekdayLabel = (day) => {
        if (!day) return "";
        return t(`fullDaysName.${day}`);
    };

    const getMonthLabel = (mIdx) => {
        const monthName = moment.months()[mIdx];
        return monthName ? t(`months.${monthName.toLowerCase()}`) : "";
    };

    const getOrdinalSuffix = (day, lang) => {
        if (lang === "nl") return "e";
        if (lang === "hi") return "";
        if (day === 1 || day === 21 || day === 31) return "st";
        if (day === 2 || day === 22) return "nd";
        if (day === 3 || day === 23) return "rd";
        return "th";
    };

        const getRecurrenceSummary = () => {
        const { frequency, selectedDays, monthlyType, monthlyDay, monthlyOccurrences, yearlyDates, oneTimeDate, dailyTimes } = taskForm;
        if (!frequency) return '';

        const dayNames = selectedDays
            .slice()
            .sort((a, b) => DAY_KEYS.indexOf(a) - DAY_KEYS.indexOf(b))
            .map((day) => getWeekdayLabel(day))
            .join(' , ');

        switch (frequency) {
            case 'OneTime': {
                const targetDate = oneTimeDate || startDate;
                if (!targetDate) return '';
                const formattedDate = moment(targetDate).format('Do MMMM YYYY');
                return t("recurrenceSummaryOnce", { date: formattedDate });
            }
            case 'Daily': {
                const times = (dailyTimes && dailyTimes.length > 0) ? dailyTimes : [getDefaultDailyTime()];
                const timesStr = times.map(t => moment(t).format("HH:mm")).join(", ");
                if (times.length <= 1) {
                    return t("recurrenceSummaryDailySingleAlt", { time: timesStr });
                }
                return t("recurrenceSummaryDailyMultiAlt", { count: times.length, times: timesStr });
            }
            case 'Weekly': {
                if (!selectedDays.length) return '';
                return t("recurrenceSummaryWeeklyAlt", { days: dayNames });
            }
            case 'Monthly': {
                if (monthlyType === 'specific_day') {
                    const dayText = monthlyDay === "last" ? t("recurrenceSummaryMonthlyLastDay") : t("recurrenceSummaryMonthlyDayN", { day: monthlyDay });
                    return t("recurrenceSummaryMonthlyDayText", { dayText });
                } else {
                    const desc = (monthlyOccurrences || [])
                        .map(o => `${t(o.occurrence)} ${getWeekdayLabel(o.weekday)}`)
                        .join(` ${t("listConjunction") || "and"} `);
                    return t("recurrenceSummaryMonthlyOccurrence", { desc });
                }
            }
            case 'Yearly': {
                const desc = (yearlyDates || [])
                    .map(d => {
                        const monthsStr = getMonthLabel(d.month);
                        if (d.day === "last") return t("recurrenceSummaryYearlyLastDay", { month: monthsStr });
                        const suffix = getOrdinalSuffix(d.day, i18n.language);
                        return t("recurrenceSummaryYearlyDate", { day: d.day, suffix, month: monthsStr });
                    })
                    .join(` ${t("listConjunction") || "and"} `);
                return t("recurrenceSummaryYearly", { desc });
            }
            default: return '';
        }
    };

    const handleAddTask = () => {
        if (!isTaskValid) return;
        const taskLabel = taskOptions.find(o => o.value === taskForm.taskId)?.label || taskForm.taskId;

    const advancedRecurrence = {};
        if (taskForm.frequency === "Daily") {
            advancedRecurrence.times = (taskForm.dailyTimes || [])
                .slice()
                .sort((a, b) => moment(a).valueOf() - moment(b).valueOf())
                .map((t) => localTimeToUtcHHmm(t));
        } else if (taskForm.frequency === "Monthly") {
            advancedRecurrence.monthlyType = taskForm.monthlyType;
            if (taskForm.monthlyType === "specific_day") {
                advancedRecurrence.monthlyDay = taskForm.monthlyDay;
            } else {
                advancedRecurrence.monthlyOccurrences = taskForm.monthlyOccurrences;
            }
        } else if (taskForm.frequency === "Yearly") {
            advancedRecurrence.dates = taskForm.yearlyDates;
        } else if (taskForm.frequency === "OneTime") {
            advancedRecurrence.date = taskForm.oneTimeDate
                ? moment(taskForm.oneTimeDate).format("YYYY-MM-DD")
                : null;
        }

        const selectedDaysVal = taskForm.frequency === "Weekly"
            ? taskForm.selectedDays
            : (["Daily", "Monthly", "Yearly", "OneTime"].includes(taskForm.frequency) ? advancedRecurrence : null);

        setSavedTasks(prev => [...prev, {
            ...taskForm,
            id: uuidv4(),
            taskName: taskLabel,
            SelectedDays: selectedDaysVal && typeof selectedDaysVal === 'object' ? JSON.stringify(selectedDaysVal) : selectedDaysVal,
            summary: getRecurrenceSummary(),
        }]);
        setTaskForm(EMPTY_TASK_FORM);
    };

    const removeTask = (id) => setSavedTasks(prev => prev.filter(t => t.id !== id));

    React.useImperativeHandle(ref, () => ({
        getData: () => ({
            workType,
            startDate,
            paymentCondition: paymentConditionSelectOptions.find(o => o.value === paymentCondition) || null,
            invoiceType: invoiceTypeSelectOptions.find(o => o.value === invoiceType) || null,
            savedTasks
        }),
        validate: () => {
            if (!workType) { toast.error(t('workTypeRequired'), { position: 'top-right', autoClose: 2000 }); return false; }
            if (!startDate) { toast.error(t('startDateRequired'), { position: 'top-right', autoClose: 2000 }); return false; }
            if (savedTasks.length === 0) { toast.error(t('saveAtLeastOneTask'), { position: 'top-right', autoClose: 2000 }); return false; }
            return true;
        },
        handleAddTask
    }));

    useEffect(() => {
        onTaskStateChange?.({ savedTasksCount: savedTasks.length, isTaskValid });
    }, [savedTasks.length, isTaskValid, onTaskStateChange]);

    return (
        <div className="mb-3">
            <div className="d-flex align-items-center gap-2 mb-2">
                <span className="fw-medium text-primary font-size-15">{t('createNewProposal')}</span>
            </div>

            {/* Work Type + Start Date */}
            <div className="row g-3 mb-3">
                <div className="col-6">
                    <Form.Label className="text-muted fw-medium mb-1 font-size-13">
                        {t('workType')} <span className="text-danger">*</span>
                    </Form.Label>
                    <Select
                        options={workTypeOptions}
                        value={workType}
                        onChange={setWorkType}
                        placeholder={t('select')}
                        className="custom-select-container"
                        classNamePrefix="custom-select"
                        noOptionsMessage={() => t('noOptionsAvailable')}
                        {...getSelectPortalProps()}
                    />
                </div>
                <div className="col-6">
                    <Form.Label className="text-muted fw-medium mb-1 font-size-13">
                        {t('startDate')} <span className="text-danger">*</span>
                    </Form.Label>
                    <DatePicker
                        selected={startDate}
                        onChange={setStartDate}
                        dateFormat="dd-MM-yyyy"
                        placeholderText="DD-MM-YYYY"
                        popperContainer={({ children }) => ReactDOM.createPortal(children, document.body)}
                        popperProps={{ strategy: 'fixed' }}
                        popperPlacement="bottom-start"
                        customInput={
                            <div className="d-flex align-items-center border rounded-2 px-3 py-2 gap-2 cursor-pointer bg-white w-100" style={{ minHeight: '38px' }}>
                                <span className={`flex-grow-1 fw-medium font-size-13 ${startDate ? 'text-900' : 'text-muted opacity-50'}`}>
                                    {startDate ? moment(startDate).format('DD-MM-YYYY') : 'DD-MM-YYYY'}
                                </span>
                            </div>
                        }
                    />
                </div>
            </div>

            {/* Task Details Section */}
            <div>
                <div className="d-flex align-items-center gap-2 mb-3">
                    <span className="fw-medium text-primary font-size-15">{t('taskDetails')}</span>
                </div>

                <div className="mb-3">
                    <Select
                        options={taskOptions}
                        value={taskOptions.find(o => o.value === taskForm.taskId) || null}
                        onChange={selected => setTaskForm(prev => ({ ...prev, taskId: selected ? selected.value : '' }))}
                        placeholder={t('select')}
                        isDisabled={!isTaskSectionEnabled}
                        className="custom-select-container"
                        classNamePrefix="custom-select"
                        noOptionsMessage={() => t('noOptionsAvailable')}
                        {...getSelectPortalProps()}
                    />
                </div>

                <div className="mb-3">
                    <Form.Group className="mb-3">
                        <Form.Label className="text-muted fw-medium mb-1 font-size-13">
                            {t('frequency')} <span className="text-danger">*</span>
                        </Form.Label>
                        <Nav
                            variant="pills"
                            activeKey={taskForm.frequency}
                            onSelect={(selectedKey) => handleFrequencyChange({ value: selectedKey })}
                            className="frequency-tabs-nav mb-1"
                        >
                            <Nav.Item>
                                <Nav.Link eventKey="OneTime" disabled={!isTaskSectionEnabled}>{t("onetime")}</Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="Daily" disabled={!isTaskSectionEnabled}>{t("daily")}</Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="Weekly" disabled={!isTaskSectionEnabled}>{t("weekly")}</Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="Monthly" disabled={!isTaskSectionEnabled}>{t("monthly")}</Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="Yearly" disabled={!isTaskSectionEnabled}>{t("yearly")}</Nav.Link>
                            </Nav.Item>
                        </Nav>
                    </Form.Group>

                    {taskForm.frequency === 'OneTime' && (
                        <div className="recurrence-settings-card">
                            <div className="recurrence-settings-header">
                                <i className="far fa-clock" />
                                <span>{t("recurrenceSettings")}</span>
                            </div>
                            <Form.Group className="mb-0">
                                <Form.Label className="data-font-size fw-semi-bold">
                                    {t("taskDate") || "Task Date"} <span className="text-danger">*</span>
                                </Form.Label>
                                <div className="task-date-input-wrap">
                                    <DatePicker
                                        selected={taskForm.oneTimeDate}
                                        onChange={(selectedDate) => {
                                            if (!selectedDate) {
                                                setTaskForm(prev => ({ ...prev, oneTimeDate: null }));
                                                return;
                                            }
                                            const selected = moment(selectedDate).startOf('day');
                                            const today = moment().startOf('day');
                                            if (selected.isBefore(today)) {
                                                toast.error(t('taskDateCannotBePast') || 'Task date cannot be in the past.', {
                                                    position: 'top-right',
                                                    autoClose: 2500,
                                                });
                                                return;
                                            }
                                            if (startDate && selected.isBefore(moment(startDate).startOf('day'))) {
                                                toast.error(t('taskDateBeforeStartDate') || 'Task date cannot be before the start date.', {
                                                    position: 'top-right',
                                                    autoClose: 2500,
                                                });
                                                return;
                                            }
                                            setTaskForm(prev => ({ ...prev, oneTimeDate: selectedDate }));
                                        }}
                                        dateFormat="dd-MM-yyyy"
                                        className="form-control"
                                        placeholderText="dd-mm-yyyy"
                                        popperContainer={({ children }) => ReactDOM.createPortal(children, document.body)}
                                        popperPlacement="bottom-start"
                                    />
                                </div>
                            </Form.Group>
                        </div>
                    )}

                    {taskForm.frequency === 'Daily' && (
                        <div className="recurrence-settings-card">
                            <div className="recurrence-settings-header">
                                <i className="far fa-clock" />
                                <span>{t("recurrenceSettings")}</span>
                            </div>
                            <Form.Group className="mb-0">
                                <Form.Label className="data-font-size fw-semi-bold d-block">
                                    {t("startsAt") || "Starts at"} <span className="text-danger">*</span>
                                </Form.Label>
                                 <div className="d-flex flex-column gap-2 mb-2 align-items-start">
                                    {(taskForm.dailyTimes || [getDefaultDailyTime()]).map((time, idx) => (
                                        <div key={idx} className="d-inline-flex align-items-center gap-2">
                                            <div className="daily-time-input-wrap">
                                                <DatePicker
                                                    selected={time}
                                                    onChange={(newTime) => updateDailyTimeSlot(idx, newTime)}
                                                    showTimeSelect
                                                    showTimeSelectOnly
                                                    timeIntervals={15}
                                                    timeCaption="Time"
                                                    dateFormat="HH:mm"
                                                    timeFormat="HH:mm"
                                                    placeholderText="HH:mm"
                                                    className="form-control"
                                                    popperContainer={({ children }) => ReactDOM.createPortal(children, document.body)}
                                                    popperProps={{ strategy: 'fixed' }}
                                                    customInput={
                                                        <TimeInput className="form-control" />
                                                    }
                                                />
                                            </div>
                                            {(taskForm.dailyTimes || []).length > 1 && (
                                                <button
                                                    type="button"
                                                    className="remove-day-btn"
                                                    onClick={() => removeDailyTimeSlot(idx)}
                                                    aria-label={t("remove") || "Remove"}
                                                >
                                                    &times;
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                                {(taskForm.dailyTimes || []).length < MAX_DAILY_TIME_SLOTS && (
                                    <Button
                                        variant="link"
                                        size="sm"
                                        className="p-0 mt-1 text-decoration-none add-day-link"
                                        onClick={addDailyTimeSlot}
                                    >
                                        {t("addAnotherTime") || "+ Add another time"}
                                    </Button>
                                )}
                            </Form.Group>
                        </div>
                    )}
                    {taskForm.frequency === 'Weekly' && (
                        <div className="recurrence-settings-card">
                            <div className="recurrence-settings-header">
                                <i className="far fa-clock" />
                                <span>{t("recurrenceSettings")}</span>
                            </div>
                            <Form.Group className="mb-0">
                                <Form.Label className="data-font-size fw-semi-bold">
                                    {t("weekday")} <span className="text-danger">*</span>
                                </Form.Label>
                                <div className="d-flex flex-wrap gap-2">
                                    {DAY_KEYS.map((dayKey) => (
                                        <Button
                                            key={dayKey}
                                            variant={taskForm.selectedDays.includes(dayKey) ? "success" : "outline-secondary"}
                                            size="sm"
                                            className="weekday-btn"
                                            disabled={!isTaskSectionEnabled}
                                            onClick={() => toggleDay(dayKey)}
                                        >
                                            {t(`days.${dayKey}`)}
                                        </Button>
                                    ))}
                                </div>
                            </Form.Group>
                        </div>
                    )}

                    {taskForm.frequency === 'Monthly' && (
                        <div className="recurrence-settings-card">
                            <div className="recurrence-settings-header">
                                <i className="far fa-clock" />
                                <span>{t("recurrenceSettings")}</span>
                            </div>
                            <Form.Group className="mb-3">
                                <Form.Label className="data-font-size fw-semi-bold">
                                    {t("repeatBy") || "Repeat By"} <span className="text-danger">*</span>
                                </Form.Label>
                                <Row className="g-2">
                                    <Col xs={6}>
                                        <div
                                            className={`recurrence-radio-card ${taskForm.monthlyType === "weekday_occurrence" ? "active" : ""}`}
                                            onClick={() => setTaskForm(prev => ({ ...prev, monthlyType: "weekday_occurrence" }))}
                                        >
                                            <Form.Check
                                                type="radio"
                                                name="monthlyTypeRadio"
                                                checked={taskForm.monthlyType === "weekday_occurrence"}
                                                onChange={() => setTaskForm(prev => ({ ...prev, monthlyType: "weekday_occurrence" }))}
                                                className="mb-0"
                                            />
                                            <span>{t("dayOfWeek") || "Day of Week"}</span>
                                        </div>
                                    </Col>
                                    <Col xs={6}>
                                        <div
                                            className={`recurrence-radio-card ${taskForm.monthlyType === "specific_day" ? "active" : ""}`}
                                            onClick={() => setTaskForm(prev => ({ ...prev, monthlyType: "specific_day" }))}
                                        >
                                            <Form.Check
                                                type="radio"
                                                name="monthlyTypeRadio"
                                                checked={taskForm.monthlyType === "specific_day"}
                                                onChange={() => setTaskForm(prev => ({ ...prev, monthlyType: "specific_day" }))}
                                                className="mb-0"
                                            />
                                            <span>{t("dayOfMonth") || "Day of Month"}</span>
                                        </div>
                                    </Col>
                                </Row>
                            </Form.Group>

                            {taskForm.monthlyType === 'specific_day' ? (
                                <Row className="g-3 mb-2">
                                    <Col xs={12} sm={5}>
                                        <Form.Group>
                                            <Form.Label className="data-font-size fw-semi-bold">
                                                {t("onDay") || "On Day"} <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Select
                                                value={taskForm.monthlyDay}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    setTaskForm(prev => ({ ...prev, monthlyDay: val === "last" ? "last" : Number(val) }));
                                                }}
                                                className="recurrence-inline-select"
                                            >
                                                {[...Array(31).keys()].slice(1).map((num) => (
                                                    <option key={num} value={num}>{num}</option>
                                                ))}
                                                <option value="last">{t("lastDay") || "Last Day"}</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                </Row>
                            ) : (
                                <Form.Group className="mb-2">
                                    <Form.Label className="data-font-size fw-semi-bold">
                                        {t("weekday")} <span className="text-danger">*</span>
                                    </Form.Label>
                                    {(taskForm.monthlyOccurrences || []).map((occ, idx) => (
                                        <div key={idx} className="recurrence-occurrence-row">
                                            <Form.Select
                                                value={occ.occurrence}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    setTaskForm(prev => {
                                                        const arr = [...prev.monthlyOccurrences];
                                                        arr[idx] = { ...arr[idx], occurrence: val };
                                                        return { ...prev, monthlyOccurrences: arr };
                                                    });
                                                }}
                                                className="w-auto"
                                            >
                                                <option value="1st">{t("1st") || "1st"}</option>
                                                <option value="2nd">{t("2nd") || "2nd"}</option>
                                                <option value="3rd">{t("3rd") || "3rd"}</option>
                                                <option value="4th">{t("4th") || "4th"}</option>
                                                <option value="last">{t("last") || "Last"}</option>
                                            </Form.Select>

                                            <div className="d-flex flex-nowrap gap-1">
                                                {DAY_KEYS.map((dayKey) => (
                                                    <Button
                                                        key={dayKey}
                                                        variant={occ.weekday === dayKey ? "success" : "outline-secondary"}
                                                        size="sm"
                                                        className="weekday-btn"
                                                        onClick={() => {
                                                            setTaskForm(prev => {
                                                                const arr = [...prev.monthlyOccurrences];
                                                                arr[idx] = { ...arr[idx], weekday: dayKey };
                                                                return { ...prev, monthlyOccurrences: arr };
                                                            });
                                                        }}
                                                    >
                                                        {t(`days.${dayKey}`)}
                                                    </Button>
                                                ))}
                                            </div>
                                            {(taskForm.monthlyOccurrences || []).length > 1 && (
                                                <button
                                                    type="button"
                                                    className="remove-day-btn"
                                                    onClick={() => removeMonthlyOccurrenceRow(idx)}
                                                    aria-label={t("remove") || "Remove"}
                                                >
                                                    &times;
                                                </button>
                                            )}
                                        </div>
                                    ))}

                                    {(taskForm.monthlyOccurrences || []).length < MAX_MONTHLY_OCCURRENCE_ROWS && (
                                        <Button
                                            variant="link"
                                            size="sm"
                                            className="p-0 mt-1 text-decoration-none add-day-link"
                                            onClick={addMonthlyOccurrenceRow}
                                        >
                                            {t("addAnotherDay") || "+ Add another day"}
                                        </Button>
                                    )}
                                </Form.Group>
                            )}
                        </div>
                    )}

                    {taskForm.frequency === 'Yearly' && (
                        <div className="recurrence-settings-card">
                            <div className="recurrence-settings-header">
                                <i className="far fa-clock" />
                                <span>{t("recurrenceSettings")}</span>
                            </div>
                            <Form.Group className="mb-2">
                                <Form.Label className="data-font-size fw-semi-bold">
                                    {t("onDay") || "On Day"} <span className="text-danger">*</span>
                                </Form.Label>
                                {(taskForm.yearlyDates || []).map((entry, idx) => (
                                    <div key={idx} className="recurrence-occurrence-row">
                                        <Form.Select
                                            value={entry.day}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                setTaskForm(prev => {
                                                    const arr = [...(prev.yearlyDates || [])];
                                                    arr[idx] = { ...arr[idx], day: val === "last" ? "last" : Number(val) };
                                                    return { ...prev, yearlyDates: arr };
                                                });
                                            }}
                                            className="recurrence-inline-select"
                                        >
                                            {[...Array(31).keys()].slice(1).map((num) => (
                                                <option key={num} value={num}>{num}</option>
                                            ))}
                                            <option value="last">{t("lastDay") || "Last Day"}</option>
                                        </Form.Select>
                                        <span className="text-muted font-size-13">{t("of") || "of"}</span>
                                        <Form.Select
                                            value={entry.month}
                                            onChange={(e) => {
                                                const val = Number(e.target.value);
                                                setTaskForm(prev => {
                                                    const arr = [...(prev.yearlyDates || [])];
                                                    arr[idx] = { ...arr[idx], month: val };
                                                    return { ...prev, yearlyDates: arr };
                                                });
                                            }}
                                            className="recurrence-inline-select"
                                        >
                                            {moment.months().map((month, mIdx) => (
                                                <option key={month} value={mIdx}>
                                                    {t(`shortMonths.${month.toLowerCase()}`)}
                                                </option>
                                            ))}
                                        </Form.Select>
                                        {(taskForm.yearlyDates || []).length > 1 && (
                                            <button
                                                type="button"
                                                className="remove-day-btn"
                                                onClick={() => removeYearlyDateRow(idx)}
                                                aria-label={t("remove") || "Remove"}
                                            >
                                                &times;
                                            </button>
                                        )}
                                    </div>
                                ))}

                                {(taskForm.yearlyDates || []).length < MAX_YEARLY_OCCURRENCE_ROWS && (
                                    <Button
                                        variant="link"
                                        size="sm"
                                        className="p-0 mt-1 text-decoration-none add-day-link"
                                        onClick={addYearlyDateRow}
                                    >
                                        {t("addAnotherDay") || "+ Add another day"}
                                    </Button>
                                )}
                            </Form.Group>
                        </div>
                    )}

                    {getRecurrenceSummary() && (
                        <div className="recurrence-summary-box mb-3">
                            <div className="recurrence-summary-icon">
                                <i className="fas fa-check-circle" />
                            </div>
                            <div className="recurrence-summary-content">
                                <div className="recurrence-summary-title">
                                    {t("recurrenceSummary")}
                                </div>
                                <div className="recurrence-summary-text">
                                    {getRecurrenceSummary()}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {savedTasks.length > 0 && (
                    <div className="mt-2">
                        <hr className="border-200" />
                        <div className="d-flex align-items-center gap-2 mb-3">
                            <span className="fw-medium text-primary font-size-15">{t('addedTask')} ({savedTasks.length})</span>
                        </div>
                        <div className="saved-task-list">
                            {savedTasks.map((task) => (
                                <div key={task.id} className="saved-task-row">
                                    <div className="saved-task-row-top">
                                        <span className="saved-task-name">{task.taskName}</span>
                                        <span className="badge bg-primary-subtle text-primary saved-task-badge">
                                            {t(`frequencyLabel.${task.frequency.toLowerCase()}`)}
                                        </span>
                                        <button
                                            type="button"
                                            className="saved-task-delete-btn"
                                            onClick={() => setTaskPendingDeleteId(task.id)}
                                            aria-label={`${t("delete")} ${task.taskName}`}
                                        >
                                            <FontAwesomeIcon icon={faTrash} className="font-size-12" />
                                        </button>
                                    </div>
                                    <div className="saved-task-schedule">
                                        {task.summary}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            <Modal
                show={taskPendingDeleteId !== null}
                onHide={() => setTaskPendingDeleteId(null)}
                backdrop="static"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>{t("confirmation")}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {t("confirmDeleteTaskWithSubtasks", {
                        taskName: savedTasks.find((task) => task.id === taskPendingDeleteId)?.taskName || "",
                    })}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="falcon-default" onClick={() => setTaskPendingDeleteId(null)}>
                        {t("cancel")}
                    </Button>
                    <Button
                        variant="primary"
                        onClick={() => {
                            removeTask(taskPendingDeleteId);
                            setTaskPendingDeleteId(null);
                        }}
                    >
                        {t("delete")}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
});

// ─── Shared custom input for DatePicker ───────────────────────────────────────

const DateInput = React.forwardRef(
    ({ value, placeholder, hasError, onClick, disabled }, ref) => (
        <div
            ref={ref}
            onClick={disabled ? undefined : onClick}
            className={`d-flex align-items-center border rounded-2 px-3 py-2 w-100
        ${hasError ? 'border-danger' : 'border-300'}
        ${disabled
                    ? 'bg-200 text-500 cursor-not-allowed'
                    : 'bg-white cursor-pointer'}
      `}
            style={{ minHeight: '38px' }}
        >
            <span
                className={`flex-grow-1 fw-medium font-size-13 ${disabled
                    ? 'text-500'
                    : value
                        ? 'text-900'
                        : 'text-muted opacity-50'
                    }`}
            >
                {value || placeholder}
            </span>
        </div>
    )
);

// ─── Main Component ───────────────────────────────────────────────────────────

const CreateOrderDrawer = ({ open, onClose, onCreated, selectedTimeRange, preSelectedEmployeeId, preSelectedClientId, leaveEvents = [], conflictMap = {}, fetchAllLocations = false, t = (key) => key }) => {
    const { i18n } = useTranslation();
    const [isSaving, setIsSaving] = useState(false);
    const [clientOptions, setClientOptions] = useState([]);
    const [proposalOptions, setProposalOptions] = useState([]);
    const [clientsLoading, setClientsLoading] = useState(false);
    const [proposalsLoading, setProposalsLoading] = useState(false);
    const [taskWithSubtasks, setTaskWithSubtasks] = useState([]);
    const [taskAssignments, setTaskAssignments] = useState({});
    const [recommendedEmployeeGroupsByTask, setRecommendedEmployeeGroupsByTask] = useState({});
    const [taskSelectedDays, setTaskSelectedDays] = useState({});
    const [taskOneTimeDates, setTaskOneTimeDates] = useState({});
    const [employeeList, setEmployeeList] = useState([]);
    const [workTypeOptions, setWorkTypeOptions] = useState([]);
    const [taskOptions, setTaskOptions] = useState([]);
    const [relationData, setRelationData] = useState(null);
    const [isOrderExists, setIsOrderExists] = useState(false);
    const newProposalFormRef = useRef(null);
    const [newProposalTaskState, setNewProposalTaskState] = useState({ savedTasksCount: 0, isTaskValid: false });
    const handleNewProposalTaskStateChange = useCallback((state) => {
        setNewProposalTaskState(state);
    }, []);
    const [showPlanningConflictModal, setShowPlanningConflictModal] = useState(false);
    const [pendingConflictData, setPendingConflictData] = useState(null);
    const [isInlineProposalCreated, setIsInlineProposalCreated] = useState(false);
    const [proposalVersionId, setProposalVersionId] = useState(null);
    const [conflictCheckQueue, setConflictCheckQueue] = useState([]);
    const [pendingFormData, setPendingFormData] = useState(null);
    const confirmedConflictsRef = useRef(new Set());
    const recommendationRequestRefs = useRef({});
    const recommendationDebounceRefs = useRef({});

    const getFormSignature = (formData) => {
        const s = formData?.start ? moment(formData.start).format('YYYY-MM-DD') : '';
        const e = formData?.end ? moment(formData.end).format('YYYY-MM-DD') : '';
        const st = formData?.startTime ? moment(formData.startTime).format('HH:mm') : '';
        const et = formData?.endTime ? moment(formData.endTime).format('HH:mm') : '';
        return `${s}_${st}_${e}_${et}`;
    };

    // ─── Build schema dynamically (mirrors AddNewOrder.js validation) ─────────
    const buildSchema = (tasks, orderExists) =>
        yup.object({
            client: yup.object().nullable().required(t('clientRequired')),
            proposal: yup.object().nullable().required(t('proposalRequired')),

            // Start Date — mirrors AddNewOrder editStartDate validation
            start: yup
                .date()
                .required(t('startDateRequired'))
                .typeError(t('invalidStartDate'))
                .test('startDate-validation', function (value) {
                    if (!value) return true;
                    const today = moment().startOf('day');
                    const start = moment(value).startOf('day');
                    const isAllTasksOneTime =
                        tasks.length > 0 && tasks.every(task => task?.recurrence?.frequency === 'OneTime');

                    // RULE 1 — Existing order cannot move to past
                    if (orderExists && start.isBefore(today)) {
                        return this.createError({ message: t('startDateCannotBeInPast') });
                    }
                    // RULE 2 — One-time order cannot be in past
                    if (isAllTasksOneTime && start.isBefore(today)) {
                        return this.createError({ message: t('oneTimeTaskPast') });
                    }
                    return true;
                }),

            // Start Time — mirrors AddNewOrder editStartTime validation
            startTime: yup
                .date()
                .required(t('startTimeRequired'))
                .typeError(t('invalidStartTime')),

            // End Date — mirrors AddNewOrder editEndDate validation
            end: yup
                .date()
                .nullable()
                .typeError(t('invalidEndDate'))
                .test('endDate-validation', function (endDate) {
                    const { start } = this.parent;
                    if (!start || !endDate) return true;

                    const startDay = moment(start).startOf('day');
                    const endDay = moment(endDate).startOf('day');
                    const today = moment().startOf('day');

                    if (!startDay.isValid() || !endDay.isValid()) return true;

                    const isAllTasksOneTime =
                        tasks.length > 0 && tasks.every(task => task?.recurrence?.frequency === 'OneTime');

                    // RULE 1 — End date cannot be in past
                    if (endDay.isBefore(today)) {
                        return this.createError({ message: t('endDateCannotBePast') });
                    }
                    if (isAllTasksOneTime && startDay.isBefore(today)) {
                        return this.createError({ message: t('oneTimeTaskPast') });
                    }
                    // RULE 3 — Normal: end must be after or equal to start
                    if (endDay.isBefore(startDay)) {
                        return this.createError({ message: t('endDateAfterStartDate') });
                    }
                    const oneTimeTaskDates = tasks
                        .filter(task => task?.recurrence?.frequency === 'OneTime')
                        .map(task => moment(taskOneTimeDates[task.ProposalTaskId] || task.recurrence?.advancedRecurrence?.date))
                        .filter(d => d.isValid());
                    if (oneTimeTaskDates.some(d => d.startOf('day').isAfter(endDay))) {
                        return this.createError({ message: t('endDateBeforeTaskDate') || "End date cannot be before a task's date." });
                    }
                    return true;
                }),

            // End Time — mirrors AddNewOrder editEndTime validation
            endTime: yup
                .date()
                .nullable()
                .typeError(t('invalidEndTime'))
                .test('endTime-after-startTime', t('endTimeAfterStartTime'), function (endTime) {
                    const { startTime } = this.parent;
                    if (endTime && startTime) {
                        return normalizeTime(endTime).isSameOrAfter(normalizeTime(startTime));
                    }
                    return true;
                }),
        });

    const { control, handleSubmit, watch, reset, setValue, trigger, formState: { errors } } = useForm({
        mode: 'onChange',
        resolver: yupResolver(buildSchema(taskWithSubtasks, isOrderExists)),
        defaultValues: { client: null, proposal: null, start: null, startTime: null, end: null, endTime: null }
    });

    const watchedStart = watch('start');
    const watchedEnd = watch('end');
    const watchedStartTime = watch('startTime');
    const watchedClient = watch('client');
    const watchedProposal = watch('proposal');

useEffect(() => {
        if (!preSelectedEmployeeId || taskWithSubtasks.length === 0) return;

        const employee = employeeList.find(e => e.UserId === preSelectedEmployeeId);
        if (!employee) return;

        const assignSequentially = () => {
            taskWithSubtasks.forEach(task => {
                const alreadyAssigned = (taskAssignments[task.ProposalTaskId] || []).length > 0;
                if (alreadyAssigned) return;
                setTaskAssignments(prev => ({ ...prev, [task.ProposalTaskId]: [employee.UserId] }));
            });
        };
        assignSequentially();
    }, [taskWithSubtasks, preSelectedEmployeeId, employeeList]);

    useEffect(() => {
        if (taskWithSubtasks.length === 0) return;

        const dailyTask = taskWithSubtasks.find(
            (task) => task.recurrence?.frequency === 'Daily' && Array.isArray(task.recurrence?.advancedRecurrence?.times) && task.recurrence.advancedRecurrence.times.length > 0
        );
        if (!dailyTask) return;

        const localHHmm = utcHHmmToLocal(dailyTask.recurrence.advancedRecurrence.times[0]);
        const [hour, minute] = localHHmm.split(':').map(Number);
        const fieldValue = moment().hours(hour || 0).minutes(minute || 0).seconds(0).milliseconds(0).toDate();
        setValue('startTime', fieldValue);
    }, [taskWithSubtasks, setValue]);

    const isNewProposal = watchedProposal?.value === 'New';

    // ─── Reset on drawer close ────────────────────────────────────────────────
    useEffect(() => {
        if (!open) {
            reset();
            setTaskWithSubtasks([]);
            setTaskAssignments({});
            setRecommendedEmployeeGroupsByTask({});
            recommendationDebounceRefs.current = {};
            recommendationRequestRefs.current = {};
            setTaskSelectedDays({});
            setTaskOneTimeDates({});
            setProposalOptions([]);
            setRelationData(null);
            setIsOrderExists(false);
            setIsInlineProposalCreated(false);
            confirmedConflictsRef.current.clear();
        }
    }, [open]);

    useEffect(() => {
        if (!open) return;
        if (selectedTimeRange?.start) {
            const d = new Date(selectedTimeRange.start);
            setValue('start', d);
            // Only pre-fill startTime if it's NOT a date-only selection (resource view click)
            setValue('startTime', selectedTimeRange.dateOnly ? null : d);
        } else {
            // Default start date to today when no time range is pre-selected
            setValue('start', moment().startOf('day').toDate());
            setValue('startTime', null);
        }
        if (selectedTimeRange?.dateOnly && selectedTimeRange?.start) {
            setValue('end', new Date(selectedTimeRange.start));
            setValue('endTime', null);
        } else if (selectedTimeRange?.end) {
            const d = new Date(selectedTimeRange.end);
            setValue('end', d);
            setValue('endTime', d);
        } else {
            setValue('end', null);
            setValue('endTime', null);
        }
    }, [open, selectedTimeRange]);

    useEffect(() => {


        if (!open || !preSelectedClientId || clientOptions.length === 0) {

            return;
        }

        const matchedClient = clientOptions.find(
            (opt) => opt.value === preSelectedClientId
        );
        if (matchedClient) {
            setValue('client', matchedClient);
        } else {
            console.warn("❌ No match found for preSelectedClientId:", preSelectedClientId,
                "Available values:", clientOptions.map(o => o.value));
        }
    }, [open, preSelectedClientId, clientOptions]);

    useEffect(() => {
        if (watchedProposal?.data?.CurrentVersionId) {
            setProposalVersionId(
                watchedProposal.data.CurrentVersionId
            );
        }
    }, [watchedProposal]);

    // ─── Fetch Clients ────────────────────────────────────────────────────────
    useEffect(() => {
        if (!open) return;
        const fetchClients = async () => {
            try {
                setClientsLoading(true);
                const locationId = localStorage.getItem('LocationId');
                const customerId = localStorage.getItem('customerId');
                if (!customerId) { console.error('CustomerId is missing.'); return; }
                const res = await axios.get(
                    fetchAllLocations
                        ? `${process.env.REACT_APP_API_URL}/relationRoute/relations/all`
                        : `${process.env.REACT_APP_API_URL}/relationRoute/relations`,
                    {
                        params: fetchAllLocations ? { customerId } : { locationId, customerId },
                        headers: { Authorization: process.env.REACT_APP_BasicAuthSecret }
                    }
                );
                const options = (res.data || [])
                    .sort((a, b) => (a.Name || '').localeCompare(b.Name || ''))
                    .map(c => ({ value: c.RelationNumber, label: c.Name, data: c }));
                setClientOptions(options);
            } catch (err) {
                console.error('Error fetching clients:', err);
            } finally {
                setClientsLoading(false);
            }
        };
        fetchClients();
    }, [open]);

    // ─── Fetch Employees ──────────────────────────────────────────────────────
    useEffect(() => {
        if (!open) return;
        const fetchEmployees = async () => {
            try {
                const locationId = localStorage.getItem('LocationId');
                const res = await axios.get(
                    `${process.env.REACT_APP_API_URL}/userRoute/users`,
                    { params: { locationId }, headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
                );
                const sortedEmployees = (res.data || [])
                    .filter(u => u.Status === '0' && !u.IsDelete)
                    .sort((a, b) => (a.Name || '').localeCompare(b.Name || ''));
                setEmployeeList(sortedEmployees);
            } catch (err) {
                console.error('Error fetching employees:', err);
            }
        };
        fetchEmployees();
    }, [open]);

    // ─── Fetch Work Types + Tasks ─────────────────────────────────────────────
    useEffect(() => {
        if (!open) return;
        const fetchFormOptions = async () => {
            try {
                const customerId = localStorage.getItem('customerId');
                if (!customerId) { console.error('CustomerId is missing.'); return; }
                const [templatesData, tasksRes] = await Promise.all([
                    fetchTemplatesByCustomer(i18n.language),
                    axios.get(`${process.env.REACT_APP_API_URL}/taskRoute/tasks`, {
                        params: { customerId },
                        headers: { Authorization: process.env.REACT_APP_BasicAuthSecret },
                    }),
                ]);
                setWorkTypeOptions((templatesData || []).map(tmpl => ({ value: tmpl.TemplateId, label: tmpl.TemplateName, workType: tmpl.WorkType })));
                setTaskOptions((tasksRes.data || []).map(task => ({ value: task.TaskId, label: task.TaskName })));
            } catch (err) {
                console.error('Error fetching form options:', err);
            }
        };
        fetchFormOptions();
    }, [open, i18n.language]);

    // ─── Check Existing Order ─────────────────────────────────────────────────
    const checkExistingOrder = async (proposalId, relationId) => {
        try {
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/orderRoute/checkUpcomingOrder`,
                { params: { proposalId, relationId }, headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
            );
            setIsOrderExists(res.data.exists);
        } catch (err) {
            console.error('Failed to check existing order', err);
        }
    };

    // ─── Toast warning when order already exists ──────────────────────────────
    useEffect(() => {
        if (isOrderExists) {
            toast.warn(t('orderExistsWarning'), {
                position: 'top-right',
                autoClose: 3000,
                icon: <i className="fas fa-exclamation-triangle text-warning" />,
                className: 'bg-warning-subtle text-warning border-start border-warning border-4',
                progressClassName: 'bg-warning',
            });
        }
    }, [isOrderExists]);

    // ─── Reset isOrderExists on proposal change ───────────────────────────────
    useEffect(() => {
        setIsOrderExists(false);
    }, [watchedProposal]);

    // ─── Fetch Proposals by Client ────────────────────────────────────────────
    useEffect(() => {
        if (!watchedClient?.value) {
            setProposalOptions([]);
            setTaskWithSubtasks([]);
            setTaskAssignments({});
            setTaskSelectedDays({});
            setTaskOneTimeDates({});
            setRelationData(null);
            return;
        }

        const fetchRelationData = async () => {
            try {
                const res = await axios.get(
                    `${process.env.REACT_APP_API_URL}/relationRoute/relationByNumber/${watchedClient.value}`,
                    { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
                );
                setRelationData(res.data);
            } catch (err) { console.error('Error fetching relation data:', err); }
        };
        fetchRelationData();

        const fetchProposals = async () => {
            try {
                setProposalsLoading(true);
                const locationId = localStorage.getItem('LocationId');
                const res = await axios.get(
                    `${process.env.REACT_APP_API_URL}/proposalRoute/getProposalsByClient/${locationId}/${watchedClient.value}`,
                    { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
                );
                const approved = (res.data || []).filter(p => p.Status === '1' || p.Status === 'approved');
                const options = approved.length === 0
                    ? [{ value: 'New', label: t('new') || 'New', data: null }]
                    : [
                        { label: t('actions'), options: [{ value: 'New', label: t('new') || 'New', data: null }], id: 'actions' },
                        { label: t('proposals'), options: approved.map(p => ({ value: p.ProposalId, label: `${p.TemplateName} - ${p.ProposalNumber}`, data: p })), id: 'proposals' },
                    ];
                setProposalOptions(options);
            } catch (err) {
                console.error('Error fetching proposals:', err);
            } finally {
                setProposalsLoading(false);
            }
        };
        fetchProposals();
    }, [watchedClient]);

    useEffect(() => {
        if (!watchedProposal || isInlineProposalCreated) return;

        // existing proposal selected
        if (watchedStart) {
            const start = moment(watchedStart);

            // same logic as backend
            const isAllTasksOneTime =
                taskWithSubtasks.length > 0 &&
                taskWithSubtasks.every(task => task?.recurrence?.frequency === 'OneTime');

            if (selectedTimeRange?.start) {
                setValue('end', new Date(selectedTimeRange.start));
            }
        }
    }, [watchedProposal, watchedStart, taskWithSubtasks]);

    // ─── Fetch Tasks for selected proposal ───────────────────────────────────
    useEffect(() => {
        if (!watchedProposal?.value || watchedProposal.value === 'New') {
            setTaskWithSubtasks([]);
            setTaskAssignments({});
            setTaskSelectedDays({});
            setTaskOneTimeDates({});
            return;
        }

        if (watchedClient?.value) {
            checkExistingOrder(watchedProposal.value, watchedClient.value);
        }

        const fetchSubtasksForTasks = async (tasks) => {
            const fetched = await Promise.all(
                tasks.map(async (task) => {
                    try {
                        const [taskDetailsRes, subtasksRes] = await Promise.all([
                            axios.get(`${process.env.REACT_APP_API_URL}/taskRoute/getTaskById/${task.TaskId}`, { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }),
                            axios.get(
                                task.ProposalTaskId
                                    ? `${process.env.REACT_APP_API_URL}/proposalRoute/proposalTasks/${task.ProposalTaskId}/subtasks`
                                    : `${process.env.REACT_APP_API_URL}/subtaskRoute/subtasks/${task.TaskId}`,
                                { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
                            )
                        ]);
                        return {
                            ...task,
                            taskName: taskDetailsRes.data?.TaskName || t("unnamedTask"),
                            recurrence: {
                                frequency: task.Frequency,
                                repeatEvery: task.RepeatEvery,
                                selectedDays: (() => {
                                    try {
                                        const raw = task.SelectedDays;
                                        if (Array.isArray(raw)) return raw;
                                        if (typeof raw === 'string') {
                                            const parsed = JSON.parse(raw);
                                            if (Array.isArray(parsed)) return parsed;
                                            if (typeof parsed === 'object' && parsed !== null) {
                                                return parsed.selectedDays || [];
                                            }
                                        }
                                        return [];
                                    } catch { return []; }
                                })(),
                                monthlyDay: task.MonthlyDay,
                                advancedRecurrence: (() => {
                                    try {
                                        const raw = task.SelectedDays;
                                        if (typeof raw === "string") {
                                            const parsed = JSON.parse(raw);
                                            if (typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)) {
                                                return parsed;
                                            }
                                        }
                                    } catch {}
                                    return null;
                                })()
                            },
                            Subtasks: (subtasksRes.data || []).filter((subtask) => subtask.selected !== false).map((subtask) => ({
                                ...subtask,
                                SubtaskId: subtask.id || subtask.SubtaskId,
                                Subtask: subtask.name || subtask.Subtask || subtask.SubtaskName,
                            }))
                        };
                    } catch (err) {
                        console.error('Error fetching task/subtasks:', err);
                        return { ...task, Subtasks: [], taskName: 'Unknown', recurrence: {} };
                    }
                })
            );
            setTaskWithSubtasks(fetched);
            setTaskAssignments({});
            const init = {};
            fetched.forEach(task => { init[task.ProposalTaskId] = task.recurrence?.selectedDays || []; });
            setTaskSelectedDays(init);

            const oneTimeInit = {};
            fetched.forEach(task => {
                if (task.recurrence?.frequency !== 'OneTime') return;
                const rawDate = task.recurrence?.advancedRecurrence?.date;
                oneTimeInit[task.ProposalTaskId] = rawDate ? new Date(rawDate) : null;
            });
            setTaskOneTimeDates(oneTimeInit);
        };

        const fetchTasks = async (proposalId) => {
            try {
                const res = await axios.get(
                    `${process.env.REACT_APP_API_URL}/proposalRoute/${proposalId}/tasks`,
                    { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
                );
                await fetchSubtasksForTasks(res.data || []);
            } catch (err) {
                console.error('Error fetching proposal tasks:', err);
            }
        };

        fetchTasks(watchedProposal.value);
    }, [watchedProposal]);

    // ─── Submit ───────────────────────────────────────────────────────────────
    const saveOrder = async (formData, assignmentsOverride = null) => {
        const assignments = assignmentsOverride || taskAssignments;
        if (formData.proposal?.value === 'New') {
            toast.info(t('redirectingToNewProposal') || 'Please create a new proposal first.', { position: 'top-right', autoClose: 2000 });
            return;
        }

        const hasAssignment = Object.values(assignments).some(x => x?.length > 0);
        if (taskWithSubtasks.length > 0 && !hasAssignment) {
            toast.error(t('assignAtLeastOneEmployee'), { position: 'top-right', autoClose: 2000 });
            return;
        }

        for (const task of taskWithSubtasks) {
            const freq = task.recurrence?.frequency;
            if (
                freq === 'Weekly'  &&
                (!taskSelectedDays[task.ProposalTaskId] || taskSelectedDays[task.ProposalTaskId].length === 0)
            ) {
                toast.error(t('selectAtLeastOneWeekday'), { position: 'top-right', autoClose: 2000 });
                return;
            }
        }

        setIsSaving(true);
        try {
            const userId = localStorage.getItem('UserId');
            const locationId = localStorage.getItem('LocationId');
            const customerId = localStorage.getItem('customerId');
            if (!locationId || !customerId) { setIsSaving(false); return; }

            const startDateRaw = formData.start;
            const endDateRaw = formData.end;
            const startTimeRaw = formData.startTime;
            const endTimeRaw = formData.endTime;

            if (!startDateRaw) {
                toast.error(t('startDateRequired'), { position: 'top-right', autoClose: 2000 });
                setIsSaving(false);
                return;
            }

            const proposalStart = moment(startDateRaw);
            const today = moment().startOf('day');

            const proposalStartDay = moment(proposalStart).startOf('day');
            for (const task of taskWithSubtasks) {
                if (task.recurrence?.frequency !== 'OneTime') continue;

                const effectiveDateRaw = taskOneTimeDates[task.ProposalTaskId] || task.recurrence?.advancedRecurrence?.date;
                const effectiveDate = moment(effectiveDateRaw).startOf('day');
                if (!effectiveDate.isValid()) continue;

                if (effectiveDate.isBefore(today)) {
                    toast.error(t('taskDateCannotBePast') || 'Task date cannot be in the past.', {
                        position: 'top-right',
                        autoClose: 2500,
                    });
                    setIsSaving(false);
                    return;
                }

                if (effectiveDate.isBefore(proposalStartDay)) {
                    toast.error(t('taskDateBeforeStartDate') || 'Task date cannot be before the start date.', {
                        position: 'top-right',
                        autoClose: 2500,
                    });
                    setIsSaving(false);
                    return;
                }
            }

            const isAllTasksOneTime =
                taskWithSubtasks.length > 0 &&
                taskWithSubtasks.every(task => task?.recurrence?.frequency === 'OneTime');

            let proposalEnd;
            if (isAllTasksOneTime) {
                proposalEnd = moment(proposalStart);
            } else if (endDateRaw && moment(endDateRaw).isValid()) {
                proposalEnd = moment(endDateRaw);
            } else {
                proposalEnd = moment(proposalStart).add(6, 'months');
            }

            const oneTimeTaskDatesForEnd = taskWithSubtasks
                .filter(task => task?.recurrence?.frequency === 'OneTime')
                .map(task => moment(taskOneTimeDates[task.ProposalTaskId] || task.recurrence?.advancedRecurrence?.date))
                .filter(d => d.isValid());
            const latestOneTimeTaskDate = oneTimeTaskDatesForEnd.length > 0
                ? moment.max(oneTimeTaskDatesForEnd)
                : null;
            if (latestOneTimeTaskDate && proposalEnd.isBefore(latestOneTimeTaskDate)) {
                proposalEnd = latestOneTimeTaskDate;
            }

            const orderStart = proposalStart.isBefore(today) ? today : proposalStart;
            const orderEnd = proposalEnd;
            const startTimeMoment = startTimeRaw ? moment(startTimeRaw) : null;
            const endTimeMoment = endTimeRaw ? moment(endTimeRaw) : null;
            const newOrderId = uuidv4();

            const orderPayload = {
                OrderId: newOrderId,
                CustomerId: customerId,
                OrderNumber: generateOrderNumber(),
                ProposalId: formData.proposal?.value,
                RelationId: formData.client?.value,
                Location: locationId,
                StartDate: orderStart.format('YYYY-MM-DD'),
                EndDate: orderEnd.format('YYYY-MM-DD'),
                Status: 'Draft',
                Notes: '',
                CreatedAt: moment().toISOString(),
                CreatedBy: userId,
                StartTime: startTimeMoment ? startTimeMoment.toISOString() : null,
                EndTime: endTimeMoment ? endTimeMoment.toISOString() : null,
                ProposalVersionId: proposalVersionId,
            };

            const orderDetailsPayload = [];

            const createOrderDetail = (task, cleanerId, taskDate) => {
                const taskStartMoment = startTimeMoment ? moment(startTimeMoment) : null;
                const taskEndMoment = endTimeMoment
                    ? moment(endTimeMoment)
                    : (taskStartMoment ? moment(taskStartMoment).add(1, 'hour') : null);
                return {
                    OrderDetailId: uuidv4(),
                    OrderId: newOrderId,
                    TaskId: task.TaskId,
                    ProposalTaskId: task.ProposalTaskId,
                    WeekStartDate: moment(taskDate).format('YYYY-MM-DD'),
                    DayOfWeek: moment(taskDate).format('dddd').toLowerCase(),
                    StartTime: taskStartMoment ? taskStartMoment.toISOString() : null,
                    EndTime: taskEndMoment ? taskEndMoment.toISOString() : null,
                    DurationMinutes: (taskStartMoment && taskEndMoment)
                        ? taskEndMoment.diff(taskStartMoment, 'minutes')
                        : task.DurationMinutes || 60,
                    CleanerId: cleanerId,
                    Status: '0',
                    Location: null,
                    Notes: '',
                };
            };

            taskWithSubtasks.forEach((task) => {
                const assignedCleaners = assignments[task.ProposalTaskId] || [];

                //  EXISTING PROPOSAL → ignore recurrence
                if (!isInlineProposalCreated) {
                    assignedCleaners.forEach((cleanerId) => {
                        orderDetailsPayload.push(
                            createOrderDetail(task, cleanerId, proposalStart)
                        );
                    });
                    return;
                }

                //  NEW PROPOSAL → use recurrence
                const occurrenceDates = getTaskOccurrenceDates(task, orderStart, orderEnd, taskSelectedDays, taskOneTimeDates);
                occurrenceDates.forEach((taskDate) => {
                    assignedCleaners.forEach((cleanerId) => {
                        orderDetailsPayload.push(
                            createOrderDetail(task, cleanerId, taskDate)
                        );
                    });
                });
            });
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/orderRoute/saveOrder`,
                { order: orderPayload, orderDetails: orderDetailsPayload },
                { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
            );

            if (response.status === 201) {
                clearEmployeeAvailabilityCache();
                toast.success(t('orderSaved'), { position: 'top-right', autoClose: 2000 });

                const capturedTaskWithSubtasks = taskWithSubtasks;
                const capturedOrderDetailsPayload = orderDetailsPayload;
                const capturedRelationData = relationData;
                const capturedWatchedClient = watchedClient;
                const capturedOrderPayload = orderPayload;
                const capturedNewOrderId = newOrderId;

                reset();
                setTaskWithSubtasks([]);
                setTaskAssignments({});
                setTaskSelectedDays({});
            setTaskOneTimeDates({});
                setRelationData(null);
                setIsOrderExists(false);
                onClose();
                if (onCreated) onCreated();

                // ── Notification dispatch — fire-and-forget, non-blocking ──
                (async () => {
                    try {
                        const tasksByCleaner = capturedOrderDetailsPayload.reduce((acc, detail) => {
                            if (!acc[detail.CleanerId]) acc[detail.CleanerId] = [];
                            acc[detail.CleanerId].push(detail);
                            return acc;
                        }, {});

                        const culture = localStorage.getItem('culture') || 'nl';
                        const location = [
                            capturedRelationData?.StreetName,
                            capturedRelationData?.HouseNumber,
                            capturedRelationData?.PostCode,
                            capturedRelationData?.City,
                        ].filter(Boolean).join(', ');

                        const entries = await Promise.all(
                            Object.entries(tasksByCleaner).map(async ([cleanerId, details]) => {
                                const cleaner = employeeList.find(c => c.UserId === cleanerId);
                                if (!cleaner) return null;

                                const firstTask = capturedTaskWithSubtasks.find(t => t.ProposalTaskId === details[0].ProposalTaskId);

                                const uniqueTaskMap = new Map();
                                for (const detail of details) {
                                    if (!uniqueTaskMap.has(detail.TaskId)) {
                                        const task = capturedTaskWithSubtasks.find(t => t.ProposalTaskId === detail.ProposalTaskId);
                                        const freq = task?.recurrence?.frequency?.toLowerCase();
                                        const translatedFrequency = freq === 'onetime' ? t('onetime') : t(freq);
                                        uniqueTaskMap.set(detail.TaskId, {
                                            taskName: task?.taskName,
                                            startTime: detail.StartTime ? moment(detail.StartTime).format('HH:mm') : null,
                                            endTime: detail.EndTime ? moment(detail.EndTime).format('HH:mm') : null,
                                            frequency: task?.recurrence?.frequency === 'OneTime'
                                                ? t('onetime')
                                                : `${getOccurrenceCount(task?.recurrence)}x ${translatedFrequency}`,
                                        });
                                    }
                                }

                                return {
                                    cleaner: { userId: cleanerId, name: cleaner.Name, email: cleaner.Emailaddress, phone: cleaner.PhoneNumber },
                                    data: {
                                        orderInfo: {
                                            clientName: capturedRelationData?.Name || capturedWatchedClient?.label,
                                            location,
                                            startDate: capturedOrderPayload.StartDate,
                                            endDate: capturedOrderPayload.EndDate,
                                            tasks: Array.from(uniqueTaskMap.values()),
                                        },
                                        culture,
                                        organizationName: process.env.REACT_APP_ORGANIZATION_NAME,
                                        userId,
                                        raisedFrom: userId,
                                        isActionRequired: true,
                                        sourceId: capturedNewOrderId,
                                        sourceType: 'order',
                                        sourceDate: capturedOrderPayload.StartDate,
                                        inAppMessageCleaner: t('in-AppNotifications.taskAssignedMessage', {
                                            taskName: firstTask?.taskName,
                                            clientName: capturedRelationData?.Name,
                                        }),
                                        inAppManagerSuffix: t('in-AppNotifications.taskAssignedManagerSuffix', {
                                            taskName: firstTask?.taskName,
                                            clientName: capturedRelationData?.Name,
                                        }),
                                        whatsappTemplateName: `task_assignment_marketing_${culture}`,
                                        whatsappParameters: [
                                            cleaner.Name,
                                            firstTask?.taskName,
                                            details[0].StartTime ? moment(details[0].StartTime).format('DD-MM-YYYY HH:mm') : '',
                                            location,
                                            moment(capturedOrderPayload.StartDate).format('DD-MM-YYYY'),
                                            moment(capturedOrderPayload.EndDate).format('DD-MM-YYYY'),
                                        ],
                                    }
                                };
                            })
                        );

                        await dispatchBatchNotification({
                            customerId,
                            eventKey: 'TASK_ASSIGNED',
                            entries: entries.filter(Boolean),
                        });
                    } catch (err) {
                        console.error('Order creation notification error (non-blocking):', err);
                    }
                })();
            }
        } catch (err) {
            console.error('Error creating order:', err);
            toast.error(t('orderSaveFailed'), { position: 'top-right', autoClose: 3000 });
        } finally {
            setIsSaving(false);
        }
    };

    const handleStartDateChange = (field) => (date) => {
        field.onChange(date);
        trigger('end');
    };

    const handleSaveProposal = async () => {
        if (!newProposalFormRef.current?.validate()) return;
        const { workType, startDate, savedTasks, paymentCondition, invoiceType } = newProposalFormRef.current.getData();

        setIsSaving(true);
        try {
            const userId = localStorage.getItem('UserId');
            const locationId = localStorage.getItem('LocationId');
            const customerId = localStorage.getItem('customerId');
            const clientRelationNumber = watchedClient?.value;

            if (!clientRelationNumber) {
                toast.error(t('clientRequired'), { position: 'top-right', autoClose: 2000 });
                setIsSaving(false);
                return;
            }

            const userRes = await axios.get(
                `${process.env.REACT_APP_API_URL}/userRoute/getUserById/${userId}`,
                { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
            );
            const employeeNumber = userRes?.data?.UserNumber || null;
            const now = new Date();
            const pad = (n, l = 2) => String(n).padStart(l, '0');
            const proposalNumber = `OR${pad(now.getDate())}${pad(now.getMonth() + 1)}${String(now.getFullYear()).slice(-2)}${pad(now.getHours())}${pad(now.getSeconds())}${pad(now.getMilliseconds(), 3)}`;
            const newProposalId = uuidv4();
            const formattedStartDate = moment(startDate).format('YYYY-MM-DD');
            const formattedDueDate = moment(startDate).add(14, 'days').format('YYYY-MM-DD');
            const clientData = watchedClient?.data;

            const proposalPayload = {
                proposalId: newProposalId, proposalNumber, customerId, locationId, employee: employeeNumber,
                client: clientRelationNumber, templateId: workType?.value, templateName: workType?.label,
                workType: workType?.workType, paymentTerms: paymentCondition?.value, invoiceType: invoiceType?.value,
                contactperson: clientData?.Contactperson || null, location: clientData?.City || null,
                startDate: formattedStartDate, date: formattedStartDate, dueDate: formattedDueDate,
                status: '1', clientComment: null, proposalComment: null, isImported: false,
            };

            const proposalRes = await axios.post(
                `${process.env.REACT_APP_API_URL}/proposalRoute/addProposal`,
                proposalPayload,
                { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
            );

            const createdProposalId = proposalRes.data?.ProposalId || newProposalId;
            const createdVersionId = proposalRes.data?.CurrentVersionId || null;
            setProposalVersionId(createdVersionId);

            for (const task of savedTasks) {
                const taskPayload = {
                    TaskId: task.taskId, Frequency: task.frequency,
                    RepeatEvery: task.frequency === 'OneTime' ? 0 : task.repeatEvery,
                    SelectedDays: task.SelectedDays || null,
                    MonthlyDay: task.monthlyDay || null, ProposalId: createdProposalId,
                    ProposalVersionId: createdVersionId || null,
                    bypassGate: true,
                };
                const savedTaskRes = await axios.post(
                    `${process.env.REACT_APP_API_URL}/proposalRoute/${createdProposalId}/tasks/add`,
                    taskPayload, { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
                );
               
                const proposalTaskId = savedTaskRes.data?.ProposalTaskId;
                const subtasksRes = await axios.get(
                    `${process.env.REACT_APP_API_URL}/subtaskRoute/subtasks/${task.taskId}`,
                    { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
                );
                const fetchedSubtasks = subtasksRes.data || [];
                if (fetchedSubtasks.length > 0 && proposalTaskId) {
                    await axios.post(
                        `${process.env.REACT_APP_API_URL}/proposalRoute/subtasks/replace`,
                        {
                            proposalId: createdProposalId,
                            bypassGate: true,
                            subtasks: fetchedSubtasks.map(st => ({
                                ProposalTaskId: proposalTaskId,
                                SubtaskId: st.id || st.SubtaskId,
                                SelectedDays: taskPayload.SelectedDays,
                                MonthlyDay: taskPayload.MonthlyDay
                            }))
                        },
                        { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
                    );
                }
            }

            toast.success(t('proposalTasksSaved'), { position: 'top-right', autoClose: 2000 });

            const updatedProposalsRes = await axios.get(
                `${process.env.REACT_APP_API_URL}/proposalRoute/getProposalsByClient/${locationId}/${clientRelationNumber}`,
                { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
            );
            const approved = (updatedProposalsRes.data || []).filter(p => p.Status === '1' || p.Status === 'approved');
            const newOptions = approved.length === 0
                ? [{ value: 'New', label: t('new') || 'New', data: null }]
                : [
                    { label: t('actions'), options: [{ value: 'New', label: t('new') || 'New', data: null }], id: 'actions' },
                    { label: t('proposals'), options: approved.map(p => ({ value: p.ProposalId, label: `${p.TemplateName} - ${p.ProposalNumber}`, data: p })), id: 'proposals' },
                ];
            setProposalOptions(newOptions);
            setValue('proposal', { value: createdProposalId, label: `${workType?.label} - ${proposalNumber}`, data: proposalRes.data });
            setIsInlineProposalCreated(true);
        } catch (err) {
            console.error('Error saving proposal:', err);
            toast.error(t('orderSaveFailed'), { position: 'top-right', autoClose: 3000 });
        } finally {
            setIsSaving(false);
        }
    };

    const loadRecommendationsForTask = async (task, assignedEmployeeIds) => {
        const taskId = task.ProposalTaskId;
        const requestId = (recommendationRequestRefs.current[taskId] || 0) + 1;
        recommendationRequestRefs.current[taskId] = requestId;
        const fallback = { recommended: [], others: sortEmployeesAscending(employeeList) };

        if (!assignedEmployeeIds?.length) {
            if (requestId === recommendationRequestRefs.current[taskId]) setRecommendedEmployeeGroupsByTask(prev => ({ ...prev, [taskId]: fallback }));
            return;
        }

        const orderStart = watch('start') ? moment(watch('start')) : null;
        const orderEndDate = watch('end') ? moment(watch('end')) : orderStart?.clone();
        const startTimeVal = watch('startTime');
        const endTimeVal = watch('endTime');
        const occurrenceDates = orderStart && orderEndDate
            ? getTaskOccurrenceDates(task, orderStart, orderEndDate, taskSelectedDays, taskOneTimeDates)
            : [];
        const startTimeOfDay = startTimeVal ? moment(startTimeVal) : null;
        const endTimeOfDay = startTimeOfDay
            ? (endTimeVal ? moment(endTimeVal) : startTimeOfDay.clone().add(1, 'hour'))
            : null;

        const availabilityCheck = async (employee) => {
            const inFormSlots = [];
            taskWithSubtasks
                .filter(otherTask => otherTask.ProposalTaskId !== taskId)
                .forEach(otherTask => {
                    const otherAssignedIds = taskAssignments[otherTask.ProposalTaskId] || [];
                    if (!otherAssignedIds.includes(employee.UserId) || !startTimeOfDay || !endTimeOfDay) return;

                    const dayKeys = otherTask.recurrence?.frequency === 'OneTime'
                        ? [orderStart?.format('dddd').toLowerCase()]
                        : (taskSelectedDays[otherTask.ProposalTaskId] || []);

                    dayKeys.forEach(dayKey => {
                        inFormSlots.push({
                            id: otherTask.ProposalTaskId,
                            employeeId: employee.UserId,
                            start: startTimeOfDay.toDate(),
                            end: endTimeOfDay.toDate(),
                            label: otherTask.taskName,
                            clientName: watchedClient?.label,
                            dayKey: dayKey.toLowerCase(),
                        });
                    });
                });

            return checkEmployeeAvailability({
                employeeId: employee.UserId,
                conflictMap,
                startDate: orderStart,
                endDate: orderEndDate,
                occurrenceDates,
                timeWindow: { startTimeOfDay, endTimeOfDay },
                inFormSlots,
                customerId: localStorage.getItem('customerId'),
                excludeOrderDetailId: null,
            });
        };

        try {
            const recommendations = await fetchRecommendedEmployees(
                localStorage.getItem('customerId'),
                assignedEmployeeIds
            );
            if (!recommendations) {
                if (requestId === recommendationRequestRefs.current[taskId]) setRecommendedEmployeeGroupsByTask(prev => ({ ...prev, [taskId]: fallback }));
                return;
            }

            const scopedRecommendations = recommendations.filter(recommendation =>
                employeeList.some(employee => String(employee.UserId) === String(recommendation.UserId))
            );
            const groups = await partitionRecommendedEmployees(
                employeeList,
                scopedRecommendations,
                availabilityCheck
            );
            if (requestId === recommendationRequestRefs.current[taskId]) setRecommendedEmployeeGroupsByTask(prev => ({ ...prev, [taskId]: groups }));
        } catch (error) {
            if (requestId === recommendationRequestRefs.current[taskId]) setRecommendedEmployeeGroupsByTask(prev => ({ ...prev, [taskId]: fallback }));
        }
    };

    const scheduleRecommendationsForTask = (task, employeeIds) => {
        const taskId = task.ProposalTaskId;
        clearTimeout(recommendationDebounceRefs.current[taskId]);
        recommendationDebounceRefs.current[taskId] = setTimeout(() => {
            void loadRecommendationsForTask(task, employeeIds);
        }, 275);
    };

    const handleEmployeeSelectWithLeaveCheck = async (task, selected, currentAssignments) => {
        const selectedEmployeeIds = selected.map(i => i.value);
        const newlyAdded = selected.find(s => !(currentAssignments || []).includes(s.value));
        if (!newlyAdded) {
            setTaskAssignments(prev => ({ ...prev, [task.ProposalTaskId]: selectedEmployeeIds }));
            scheduleRecommendationsForTask(task, selectedEmployeeIds);
            return;
        }

        const orderStart = watch('start') ? moment(watch('start')) : null;
        const orderEnd = watch('end') ? moment(watch('end')) : orderStart?.clone();
        const startTimeVal = watch('startTime');
        const endTimeVal = watch('endTime');

        if (!orderStart) {
            setTaskAssignments(prev => ({ ...prev, [task.ProposalTaskId]: selectedEmployeeIds }));
            scheduleRecommendationsForTask(task, selectedEmployeeIds);
            return;
        }

        const orderEndDate = orderEnd || orderStart.clone();
        const occurrenceDates = getTaskOccurrenceDates(task, orderStart, orderEndDate, taskSelectedDays, taskOneTimeDates);
        const startTimeOfDay = startTimeVal ? moment(startTimeVal) : null;
        const endTimeOfDay = startTimeVal
            ? (endTimeVal ? moment(endTimeVal) : startTimeOfDay.clone().add(1, 'hour'))
            : null;

        const inFormSlots = [];
        taskWithSubtasks
            .filter(t => t.ProposalTaskId !== task.ProposalTaskId)
            .forEach(t => {
                const assignedIds = taskAssignments[t.ProposalTaskId] || [];
                if (!assignedIds.includes(newlyAdded.value)) return;
                const dayKeys = t.recurrence?.frequency === 'OneTime'
                    ? [orderStart.format('dddd').toLowerCase()]
                    : (taskSelectedDays[t.ProposalTaskId] || []);

                dayKeys.forEach(dayKey => {
                    inFormSlots.push({
                        id: t.ProposalTaskId,
                        employeeId: newlyAdded.value,
                        start: startTimeOfDay.toDate(),
                        end: endTimeOfDay.toDate(),
                        label: t.taskName,
                        clientName: watchedClient?.label,
                        dayKey: dayKey.toLowerCase(),
                    });
                });
            });

        const conflictResult = await checkEmployeeAvailability({
            employeeId: newlyAdded.value,
            conflictMap,
            startDate: orderStart,
            endDate: orderEndDate,
            occurrenceDates,
            timeWindow: { startTimeOfDay, endTimeOfDay },
            inFormSlots,
            customerId: localStorage.getItem('customerId'),
            excludeOrderDetailId: null,
        });

        if (
            conflictResult?.type === 'leave' ||
            conflictResult?.type === 'taskConflict' ||
            conflictResult?.type === 'combined'
        ) {
            setPendingConflictData({
                taskId: task.ProposalTaskId,
                allSelected: selected,
                employeeId: newlyAdded.value,
                employeeName: newlyAdded.label,
                conflictType: conflictResult.type,
            });
            setShowPlanningConflictModal(true);
            return;
        }

        setTaskAssignments(prev => ({ ...prev, [task.ProposalTaskId]: selectedEmployeeIds }));
        scheduleRecommendationsForTask(task, selectedEmployeeIds);
    };

    const findAssignmentConflicts = async (formData) => {
        const conflicts = [];
        const orderStart = formData.start ? moment(formData.start) : null;
        if (!orderStart) return conflicts;
        const orderEndDate = formData.end ? moment(formData.end) : orderStart.clone();
        const signature = getFormSignature(formData);

        const startTimeOfDay = formData.startTime ? moment(formData.startTime) : null;
        const endTimeOfDay = startTimeOfDay
            ? (formData.endTime ? moment(formData.endTime) : startTimeOfDay.clone().add(1, 'hour'))
            : null;

        for (const task of taskWithSubtasks) {
            const assignedIds = [...new Set(taskAssignments[task.ProposalTaskId] || [])];
            if (!assignedIds.length) continue;

            const occurrenceDates = getTaskOccurrenceDates(task, orderStart, orderEndDate, taskSelectedDays, taskOneTimeDates);

            const buildInFormSlots = (employeeId) => {
                const inFormSlots = [];
                taskWithSubtasks
                    .filter(t => t.ProposalTaskId !== task.ProposalTaskId)
                    .forEach(t => {
                        const otherAssignedIds = taskAssignments[t.ProposalTaskId] || [];
                        if (!otherAssignedIds.includes(employeeId)) return;
                        const dayKeys = t.recurrence?.frequency === 'OneTime'
                            ? [orderStart.format('dddd').toLowerCase()]
                            : (taskSelectedDays[t.ProposalTaskId] || []);
                        dayKeys.forEach(dayKey => {
                            inFormSlots.push({
                                id: t.ProposalTaskId,
                                employeeId,
                                start: startTimeOfDay ? startTimeOfDay.toDate() : null,
                                end: endTimeOfDay ? endTimeOfDay.toDate() : null,
                                label: t.taskName,
                                clientName: watchedClient?.label,
                                dayKey: dayKey.toLowerCase(),
                            });
                        });
                    });
                return inFormSlots;
            };

            const perEmployeeResults = await Promise.all(
                assignedIds.map(async (employeeId) => {
                    const employee = employeeList.find(e => e.UserId === employeeId);
                    if (!employee) return null;

                    const conflictResult = await checkEmployeeAvailability({
                        employeeId,
                        conflictMap,
                        startDate: orderStart,
                        endDate: orderEndDate,
                        occurrenceDates,
                        timeWindow: { startTimeOfDay, endTimeOfDay },
                        inFormSlots: buildInFormSlots(employeeId),
                        customerId: localStorage.getItem('customerId'),
                        excludeOrderDetailId: null,
                    });

                    if (
                        conflictResult?.type === 'leave' ||
                        conflictResult?.type === 'taskConflict' ||
                        conflictResult?.type === 'combined'
                    ) {
                        const conflictKey = `${task.ProposalTaskId}_${employeeId}_${signature}`;
                        if (confirmedConflictsRef.current.has(conflictKey)) return null;
                        return {
                            taskId: task.ProposalTaskId,
                            employeeId,
                            employeeName: employee.Name,
                            conflictType: conflictResult.type,
                            signature,
                        };
                    }
                    return null;
                })
            );

            perEmployeeResults.forEach((result) => {
                if (result) conflicts.push(result);
            });
        }

        return conflicts;
    };

    const advanceConflictQueue = () => {
        setConflictCheckQueue(prev => {
            const remaining = prev.slice(1);
            if (remaining.length > 0) {
                setPendingConflictData(remaining[0]);
                setShowPlanningConflictModal(true);
            } else {
                setShowPlanningConflictModal(false);
                setPendingConflictData(null);
                setPendingFormData(null);
            }
            return remaining;
        });
    };

    const onSubmit = async (formData) => {
        const conflicts = await findAssignmentConflicts(formData);
        if (conflicts.length > 0) {
            setConflictCheckQueue(conflicts);
            setPendingFormData(formData);
            setPendingConflictData(conflicts[0]);
            setShowPlanningConflictModal(true);
            return;
        }
        await saveOrder(formData);
    };

        const CustomGroupHeading = (props) => {
        const { innerProps, ...rest } = props;
        const groupId = props.data?.id;
        const showDivider = groupId === "proposals";

        return (
            <div className="m-0 p-0">
                {showDivider && <hr className="my-1 border-300" />}
                <components.GroupHeading
                    {...rest}
                    innerProps={{
                        ...innerProps,
                        className: `px-2 py-1 m-0 text-uppercase text-600 fs--1 ${innerProps?.className || ''}`
                    }}
                />
            </div>
        );
    };

    const showDateTimeFields = (watchedProposal || (selectedTimeRange?.start)) && !isNewProposal;

    return (
        <Offcanvas show={open} onHide={onClose} placement="end" backdrop={true} scroll={true} className="new-planning-offcanvas">
            <div className="d-flex flex-column h-100 bg-white">

                {/* Header */}
                <div className="p-3 border-bottom d-flex justify-content-between align-items-center">
                    <h4 className="mb-0">{t('newPlanning')}</h4>
                    <Button variant="link" className="p-0 border-0 shadow-none text-secondary flex-shrink-0 fs-7" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </Button>
                </div>

                {/* Scrollable Body */}
                <div className="p-3 flex-grow-1 scrollbar overflow-y-auto overflow-x-visible">

                    {/* Client */}
                    {!isNewProposal && (
                        <div className="mb-3">
                            <Form.Label className="text-muted fw-medium font-size-13 mb-1">
                                {t('client')} <span className="text-danger">*</span>
                            </Form.Label>
                            <Controller
                                control={control}
                                name="client"
                                render={({ field }) => (
                                    <Select
                                        {...field}
                                        options={clientOptions}
                                        isLoading={clientsLoading}
                                        placeholder={t('searchLabel')}
                                        className={`custom-select-container ${errors.client ? 'is-invalid' : ''}`}
                                        classNamePrefix="custom-select"
                                        noOptionsMessage={() => t('noOptionsAvailable')}
                                        {...getSelectPortalProps()}
                                    />
                                )}
                            />
                            {errors.client && <div className="text-danger font-size-11 mt-1">{errors.client.message}</div>}
                        </div>
                    )}

                    {/* Proposal */}
                    {!isNewProposal && (
                        <div className="mb-3">
                            <Form.Label className="text-muted fw-medium font-size-13 mb-1">
                                {t('proposal')} <span className="text-danger">*</span>
                            </Form.Label>
                            <Controller
                                control={control}
                                name="proposal"
                                render={({ field }) => (
                                    <Select
                                        {...field}
                                        options={proposalOptions}
                                        isLoading={proposalsLoading}
                                        placeholder={t('searchLabel')}
                                        isDisabled={!watchedClient}
                                        className={`custom-select-container ${errors.proposal ? 'is-invalid' : ''}`}
                                        classNamePrefix="custom-select"
                                        noOptionsMessage={() => t('noOptionsAvailable')}
                                        components={{ GroupHeading: CustomGroupHeading }}
                                        styles={{
                                            group: (provided) => ({ ...provided, paddingTop: 0, paddingBottom: 0, margin: 0 }),
                                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                        }}
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                        menuShouldScrollIntoView={false}
                                        onChange={(selected) => field.onChange(selected)}
                                    />
                                )}
                            />
                            {errors.proposal && <div className="text-danger font-size-11 mt-1">{errors.proposal.message}</div>}
                        </div>
                    )}

                    {/* Inline New Proposal form */}
                    {isNewProposal && (
                        <NewProposalForm
                            ref={newProposalFormRef}
                            t={t}
                            workTypeOptions={workTypeOptions}
                            taskOptions={taskOptions}
                            initialStartDate={
                                selectedTimeRange?.start
                                    ? new Date(selectedTimeRange.start)
                                    : moment().startOf('day').toDate()
                            }
                            onTaskStateChange={handleNewProposalTaskStateChange}
                        />
                    )}

                    {/* ── Date & Time Fields (2×2 grid) ─────────────────────────────── */}
                    {showDateTimeFields && (
                        <div className="mb-3">

                            {/* Row 1: Start Date | Start Time */}
                            <div className="row g-3 mb-3">

                                {/* Start Date */}
                                <div className="col-6">
                                    <Form.Label className="text-muted fw-medium font-size-13 mb-1">
                                        {t('startDate')} <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Controller
                                        control={control}
                                        name="start"
                                        render={({ field }) => (
                                            <DatePicker
                                                selected={field.value}
                                                onChange={handleStartDateChange(field)}
                                                dateFormat="dd-MM-yyyy"
                                                placeholderText="DD-MM-YYYY"
                                                popperContainer={({ children }) => ReactDOM.createPortal(children, document.body)}
                                                popperPlacement="bottom-start"
                                                customInput={
                                                    <DateInput
                                                        value={field.value ? moment(field.value).format('DD-MM-YYYY') : ''}
                                                        placeholder="DD-MM-YYYY"
                                                        hasError={!!errors.start}
                                                    />
                                                }
                                            />
                                        )}
                                    />
                                    {errors.start && (
                                        <div className="text-danger font-size-11 mt-1">{errors.start.message}</div>
                                    )}
                                </div>

                                {/* Start Time */}
                                <div className="col-6">
                                    <Form.Label className="text-muted fw-medium font-size-13 mb-1">
                                        {t('startingTime') || t('startTime') || 'Start Time'} <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Controller
                                        control={control}
                                        name="startTime"
                                        render={({ field }) => (
                                            <DatePicker
                                                selected={field.value || undefined}
                                                openToDate={setHours(setMinutes(new Date(), 0), 9)}
                                                onChange={(time) => {
                                                    field.onChange(time);
                                                    trigger('endTime'); // re-validate end time
                                                }}
                                                showTimeSelect
                                                showTimeSelectOnly
                                                timeIntervals={15}
                                                timeCaption={t('time') || 'Time'}
                                                dateFormat="HH:mm"
                                                timeFormat="HH:mm"
                                                placeholderText="HH:mm"
                                                popperContainer={({ children }) => ReactDOM.createPortal(children, document.body)}
                                                popperPlacement="bottom-start"
                                                customInput={
                                                    <DateInput
                                                        value={field.value ? moment(field.value).format('HH:mm') : ''}
                                                        placeholder="HH:mm"
                                                        hasError={!!errors.startTime}
                                                    />
                                                }
                                            />
                                        )}
                                    />
                                    {errors.startTime && (
                                        <div className="text-danger font-size-11 mt-1">{errors.startTime.message}</div>
                                    )}
                                </div>
                            </div>

                            {/* Row 2: End Date | End Time */}
                            <div className="row g-3">

                                {/* End Date */}
                                <div className="col-6">
                                    <Form.Label className="text-muted fw-medium font-size-13 mb-1">
                                        {t('endDate')}
                                    </Form.Label>
                                    <Controller
                                        control={control}
                                        name="end"
                                        render={({ field }) => (
                                            <DatePicker
                                                selected={field.value}
                                                onChange={field.onChange}
                                                dateFormat="dd-MM-yyyy"
                                                placeholderText="DD-MM-YYYY"
                                                minDate={watchedStart || undefined}
                                                disabled={!!selectedTimeRange?.dateOnly && !selectedTimeRange?.isWeekView}
                                                popperContainer={({ children }) => ReactDOM.createPortal(children, document.body)}
                                                popperPlacement="bottom-start"
                                                customInput={
                                                    <DateInput
                                                        value={field.value ? moment(field.value).format('DD-MM-YYYY') : ''}
                                                        placeholder="DD-MM-YYYY"
                                                        hasError={!!errors.end}
                                                        disabled={!!selectedTimeRange?.dateOnly && !selectedTimeRange?.isWeekView}
                                                    />
                                                }
                                            />
                                        )}
                                    />
                                    {errors.end && (
                                        <div className="text-danger font-size-11 mt-1">{errors.end.message}</div>
                                    )}
                                </div>

                                {/* End Time */}
                                <div className="col-6">
                                    <Form.Label className="text-muted fw-medium font-size-13 mb-1">
                                        {t('endTime') || 'End Time'}
                                    </Form.Label>
                                    <Controller
                                        control={control}
                                        name="endTime"
                                        render={({ field }) => (
                                            <DatePicker
                                                selected={field.value || undefined}
                                                openToDate={setHours(setMinutes(new Date(), 0), 9)}
                                                onChange={field.onChange}
                                                showTimeSelect
                                                showTimeSelectOnly
                                                timeIntervals={15}
                                                timeCaption={t('time') || 'Time'}
                                                dateFormat="HH:mm"
                                                timeFormat="HH:mm"
                                                placeholderText="HH:mm"
                                                popperContainer={({ children }) => ReactDOM.createPortal(children, document.body)}
                                                popperPlacement="bottom-start"
                                                customInput={
                                                    <DateInput
                                                        value={field.value ? moment(field.value).format('HH:mm') : ''}
                                                        placeholder="HH:mm"
                                                        hasError={!!errors.endTime}
                                                    />
                                                }
                                            />
                                        )}
                                    />
                                    {errors.endTime && (
                                        <div className="text-danger font-size-11 mt-1">{errors.endTime.message}</div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Task Assignment */}
                    {taskWithSubtasks.length > 0 && (
                        <div className="mt-2">
                            <Form.Label className="text-muted fw-medium font-size-13 mb-2">
                                {t('taskAssignment')}
                            </Form.Label>
                            {taskWithSubtasks.map((task, idx) => (
                                <div key={task.ProposalTaskId} className="border rounded p-3 mb-3 bg-light">
                                    <p className="mb-2 fw-semibold font-size-13">
                                        {idx + 1}. {task.taskName} (
                                        {task.recurrence?.frequency === 'OneTime'
                                            ? t(`frequencyLabel.${task.recurrence.frequency.toLowerCase()}`)
                                            : `${getOccurrenceCount(task.recurrence)}x ${t(`frequencyLabel.${(task.recurrence?.frequency || '').toLowerCase()}`)}`}
                                        )
                                    </p>

                                    {task.recurrence?.frequency === 'OneTime' && (
                                        <div className="task-row-date-input-wrap mb-2">
                                            <DatePicker
                                                selected={taskOneTimeDates[task.ProposalTaskId] || null}
                                                onChange={(date) => {
                                                    if (!date) {
                                                        setTaskOneTimeDates((prev) => ({ ...prev, [task.ProposalTaskId]: null }));
                                                        return;
                                                    }
                                                    const selected = moment(date).startOf('day');
                                                    const today = moment().startOf('day');
                                                    if (selected.isBefore(today)) {
                                                        toast.error(t('taskDateCannotBePast') || 'Task date cannot be in the past.', {
                                                            position: 'top-right',
                                                            autoClose: 2500,
                                                        });
                                                        return;
                                                    }
                                                    if (watchedStart && selected.isBefore(moment(watchedStart).startOf('day'))) {
                                                        toast.error(t('taskDateBeforeStartDate') || 'Task date cannot be before the start date.', {
                                                            position: 'top-right',
                                                            autoClose: 2500,
                                                        });
                                                        return;
                                                    }
                                                    if (watchedEnd && selected.isAfter(moment(watchedEnd).startOf('day'))) {
                                                        toast.error(t('taskDateAfterEndDate') || 'Task date cannot be after the end date.', {
                                                            position: 'top-right',
                                                            autoClose: 2500,
                                                        });
                                                        return;
                                                    }
                                                    setTaskOneTimeDates((prev) => ({ ...prev, [task.ProposalTaskId]: date }));
                                                }}
                                                dateFormat="dd-MM-yyyy"
                                                placeholderText="dd-mm-yyyy"
                                                className="form-control form-control-sm"
                                                popperContainer={({ children }) => ReactDOM.createPortal(children, document.body)}
                                            />
                                        </div>
                                    )}

                                    {task.recurrence?.frequency !== 'OneTime' &&
                                        task.recurrence?.selectedDays?.length > 0 && (
                                            <div className="d-flex flex-wrap gap-1 mb-2">
                                                {[...task.recurrence.selectedDays]
                                                    .sort((a, b) => DAY_KEYS.indexOf(a.toLowerCase()) - DAY_KEYS.indexOf(b.toLowerCase()))
                                                    .map((day) => {
                                                        const isSelected = taskSelectedDays[task.ProposalTaskId]?.includes(day);
                                                        return (
                                                            <span
                                                                key={day}
                                                                role="button"
                                                                onClick={() => {
                                                                    setTaskSelectedDays(prev => {
                                                                        const current = prev[task.ProposalTaskId] || [];
                                                                        const updated = current.includes(day)
                                                                            ? current.filter(d => d !== day)
                                                                            : [...current, day];
                                                                        return { ...prev, [task.ProposalTaskId]: updated };
                                                                    });
                                                                }}
                                                                className={`badge fw-medium me-1 mb-1 border cursor-pointer ${isSelected ? 'bg-success-subtle text-success' : 'bg-light text-muted'}`}
                                                                style={{ cursor: 'pointer' }}
                                                            >
                                                                {t(`days.${day.toLowerCase()}`)}
                                                            </span>
                                                        );
                                                    })}
                                            </div>
                                        )}

                                    <Select
                                        isMulti
                                        closeMenuOnSelect={false}
                                        options={recommendedEmployeeGroupsByTask[task.ProposalTaskId]?.recommended?.length > 0 ? [
                                            {
                                                label: t('recommendedEmployees'),
                                                options: recommendedEmployeeGroupsByTask[task.ProposalTaskId].recommended.map(emp => ({
                                                    value: emp.UserId,
                                                    label: emp.Name,
                                                })),
                                            },
                                            {
                                                label: t('allEmployees'),
                                                options: recommendedEmployeeGroupsByTask[task.ProposalTaskId].others.map(emp => ({
                                                    value: emp.UserId,
                                                    label: emp.Name,
                                                })),
                                            },
                                        ] : sortEmployeesAscending(employeeList).map(emp => ({
                                            value: emp.UserId,
                                            label: emp.Name,
                                        }))}
                                        value={employeeList
                                            .filter(emp => taskAssignments[task.ProposalTaskId]?.includes(emp.UserId))
                                            .map(emp => ({ value: emp.UserId, label: emp.Name }))}
                                        onChange={(selected) => handleEmployeeSelectWithLeaveCheck(
                                            task,
                                            selected || [],
                                            taskAssignments[task.ProposalTaskId] || []
                                        )}
                                        placeholder={t('selectEmployees')}
                                        className="custom-select-container"
                                        classNamePrefix="custom-select"
                                        noOptionsMessage={() => t('noOptionsAvailable')}
                                        {...getSelectPortalProps()}
                                    />
                                    {taskAssignments[task.ProposalTaskId]?.length > 0 && (
                                        <div className="mt-2 d-flex flex-wrap gap-2">
                                            {taskAssignments[task.ProposalTaskId].map(id => {
                                                const employee = employeeList.find(e => e.UserId === id);
                                                return (
                                                    <div key={id} className="d-flex align-items-center rounded-pill bg-secondary-subtle px-3 py-1 small">
                                                        <FontAwesomeIcon icon="user" className="me-2 text-primary" />
                                                        <span>{employee?.Name}</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-3 border-top bg-white">
                    <div className="d-flex flex-wrap gap-2 justify-content-between align-items-center">
                        {isNewProposal ? (
                            <>
                                <Button
                                    variant="outline-secondary"
                                    className="fw-semibold rounded-3 px-4 text-nowrap"
                                    onClick={() => setValue('proposal', null)}
                                    size="sm"
                                    disabled={isSaving}
                                >
                                    {t('back')}
                                </Button>
                                <div className="d-flex flex-wrap gap-2 ms-auto">
                                    <Button
                                        variant="success"
                                        className="fw-semibold rounded-3 px-4 text-nowrap"
                                        size="sm"
                                        disabled={isSaving || !newProposalTaskState.isTaskValid}
                                        onClick={() => newProposalFormRef.current?.handleAddTask()}
                                    >
                                        + {newProposalTaskState.savedTasksCount > 0 ? t('addAnotherTask') : t('addTask')}
                                    </Button>
                                    {newProposalTaskState.savedTasksCount > 0 && (
                                        <Button
                                            variant="primary"
                                            className="fw-semibold rounded-3 px-4 text-nowrap"
                                            size="sm"
                                            disabled={isSaving}
                                            onClick={handleSaveProposal}
                                        >
                                            {isSaving ? t('saving') : (t('saveProposal') || 'Save Proposal')}
                                        </Button>
                                    )}
                                </div>
                            </>
                        ) : (
                            <>
                                <Button
                                    variant="outline-secondary"
                                    className="fw-semibold rounded-3 px-4 text-nowrap"
                                    onClick={onClose}
                                    size="sm"
                                    disabled={isSaving}
                                >
                                    {t('cancel')}
                                </Button>
                                <Button
                                    data-tour="save-btn"
                                    variant="primary"
                                    className="fw-semibold rounded-3 px-4 text-nowrap"
                                    size="sm"
                                    disabled={isSaving}
                                    onClick={handleSubmit(onSubmit)}
                                >
                                    {isSaving ? t('saving') : t('save')}
                                </Button>
                            </>
                        )}
                    </div>
                </div>

            </div>
            <PlanningConflictModal
                show={showPlanningConflictModal}
                employeeName={pendingConflictData?.employeeName}
                conflictType={pendingConflictData?.conflictType}
                t={t}
                onCancel={() => {
                    if (pendingConflictData) {
                        setTaskAssignments(prev => ({
                            ...prev,
                            [pendingConflictData.taskId]: (prev[pendingConflictData.taskId] || [])
                                .filter(id => id !== pendingConflictData.employeeId)
                        }));
                    }
                    advanceConflictQueue();
                }}
                onConfirm={() => {
                    if (pendingConflictData) {
                        const key = `${pendingConflictData.taskId}_${pendingConflictData.employeeId}_${pendingConflictData.signature}`;
                        confirmedConflictsRef.current.add(key);
                        const task = taskWithSubtasks.find(item => item.ProposalTaskId === pendingConflictData.taskId);
                        const assignmentIds = (pendingConflictData.allSelected || []).map(item => item.value);
                        setTaskAssignments(prev => ({
                            ...prev,
                            [pendingConflictData.taskId]: assignmentIds,
                        }));
                        if (task) scheduleRecommendationsForTask(task, assignmentIds);
                    }
                    advanceConflictQueue();
                }}
            />
        </Offcanvas>
    );
};

export default CreateOrderDrawer;